import React, { useState, useEffect, useRef } from "react";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Grid,
  IconButton,
  InputAdornment,
  InputBase,
  Typography,
  useMediaQuery,
  useTheme,
  Menu,
  MenuItem,
  CircularProgress,
  Modal,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  DialogTitle,
  DialogContentText,
  FormControlLabel,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import SearchIcon from "@material-ui/icons/Search";
import CheckIcon from "@material-ui/icons/Check";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import { Link, useHistory, useLocation } from "react-router-dom";
import { trackMessageSent } from "../../../../analytics/canonicalTracking";
import { Socket } from "socket.io-client";
import { useAppSelector } from "../../../../redux/typedHooks";
import type { UserInterface } from "../../../../types/user";
import { getSocket } from "../../../../utils/websocket";
import useSEO from "../../../../seo/use-seo";
import StandardPageHelmet from "../../../../seo/components/StandardPageHelmet";
import {
  getConversationsAPI,
  getMessagesAPI,
  sendMessageAPI,
  updateMessageStatusAPI,
  deleteMessageAPI,
  deleteConversationAPI,
  searchMessagesGloballyAPI,
  createGroupConversationAPI,
} from "../../../../api/messageApi";
import { showToast } from "../../../../utils/toast";
import {
  getFileByIdAPI,
  getUserProfile,
  checkUserSubscriptionAPI,
} from "../../../../api/userApi";
import {
  sendVideoRequestAPI,
  acceptVideoRequestAPI,
  rejectVideoRequestAPI,
  setVideoRequestPendingAPI,
  cancelVideoRequestAPI,
  uploadCompletedVideoAPI,
} from "../../../../api/videoRequestsApi";
import { CustomVideoRequestDialog } from "../../../elements/custom-video-request";
import RejectVideoRequestSuccessModal from "../../../elements/RejectVideoRequestSuccessModal";
import defaultAvatars from "../../../../assets/defaultAvatars.svg";
import { useMessagesStyles } from "./style";
import { useUserStatuses } from "../../../../utils/UserStatusContext";
import PlusIconSvg from "../../../../assets/svgs/PlusIcon.svg";
import SendIconSvg from "../../../../assets/svgs/SendIcon.svg";
import VerifiedShieldHelpIcon from "../../../../assets/VerifiedShieldHelpIcon";
import Picker from "@emoji-mart/react";
import data from "@emoji-mart/data";
import { SentimentSatisfiedAlt, Close as CloseIcon } from "@material-ui/icons";
import { checkFileSize } from "../../../../utils/file";
import { useKeyboardScroll } from "../../../../hooks/useKeyboardScroll";
import { DollarSign, Upload } from "lucide-react";
import CCBillPayment from "../../../elements/CCBillPayment";
import TipDialog from "../../../elements/TipDialog";
import { GroupChatModal } from "./GroupChatModal";
import UploadMediaPostModal from "../../Home/Search-panel/UploadMediaPostModal";
import {
  muteUserAPI,
  unmuteUserAPI,
  getMuteStatusAPI,
} from "../../../../api/muteApi";
import useCreditsBalance from "../../../../hooks/useCreditsBalance";
import { useGlobalMonetization } from "../../../../hooks/useGlobalMonetization";
import {
  ensureSessionExists,
  processEncryptedMessage,
  initializeE2EE,
} from "../../../../services/e2eeService";
import UpgradeModal from "../../../Header/UpgradeModal";
import useFreeMessagesStatus from "../../../../hooks/useFreeMessagesStatus";
import ArrowBackSvg from "../../../../assets/svgs/ArrowBack.svg";
import VideoRequestBtnSvg from "../../../../assets/svgs/videoRequestBtn.svg";
import TipIconSvg from "../../../../assets/images/icons/tipIcon.svg";
import { addDays, format } from "date-fns";
import EllipsisMenuButtonProfile from "../../../elements/EllipsisMenuButtons/EllipsisMenuButtonProfile";
import sendVideoRequestIcon from "../../../../assets/images/icons/sendVideoRquest.svg";
import CoinIcon from "../../../../assets/svgs/CoinIcon";
interface MessageStatusUpdate {
  messageId: string;
  status: "sent" | "delivered" | "read";
}

interface MessageDeleted {
  messageId: string;
  conversationId: string;
}

interface TypingPayload {
  userId: string;
}

interface UserStatusUpdatePayload {
  userId: string;
  status: "online" | "offline";
}

interface Participant {
  _id: string;
  firstName: string;
  lastName: string;
  username: string;
  profileImg?: string;
  lastActive?: string;
  status?: string;
  privacySettings?: {
    showOnlineStatus?: boolean;
  };
}

type ParticipantPresence = {
  _id?: string;
  lastActive?: string;
  privacySettings?: {
    showOnlineStatus?: boolean;
  };
};

interface LastMessage {
  content: string;
  senderId: string;
  type: string;
  createdAt: string;
}

interface Conversation {
  _id: string;
  isGroup: boolean;
  groupName?: string;
  groupImage?: string;
  participants: Participant[];
  participant?: Participant;
  lastMessage: LastMessage;
  unreadCount: number;
}

interface Sender {
  _id: string;
  profileImg?: string;
  username?: string;
}

interface Message {
  _id: string;
  senderId: string;
  sender: Sender;
  conversationId: string;
  content: string;
  type: "text" | "image" | "video" | "video_request" | "notification" | "tip";
  mediaUrl?: string;
  status: "sent" | "delivered" | "read";
  createdAt: string;
  conversation: {
    _id: string;
    participants: string[];
  };
  isPaid: boolean;
  price: number;
  paidBy?: string[];
  tipAmount?: number;
  videoRequest?: {
    _id: string;
    description: string;
    price: number;
    status: "pending" | "accepted" | "rejected" | "completed" | "cancelled";
    videoUrl?: string;
    createdAt: string;
  };
  // E2EE fields
  isEncrypted?: boolean;
  encryptedContent?: string;
  messageNumber?: number;
  sessionId?: string;
}

interface ProfileData {
  _id: string;
  firstName: string;
  lastName: string;
  username: string;
  suggestedTips?: number[];
  minimumTipAmount?: number;
  customContentPrice?: number;
  privacySettings?: {
    allowMessagesWithoutSubscription?: boolean;
  };
}

const MessagesPage: React.FC = () => {
  const seo = useSEO("messages");
  const classes = useMessagesStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const user = useAppSelector<UserInterface | null>(
    state => state.user.currentUser
  );
  const userId = user?.id;
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [selectedConversationId, setSelectedConversationId] = useState<
    string | null
  >(null);
  const [inputValue, setInputValue] = useState<string>("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [messagesPage, setMessagesPage] = useState<number>(1);
  const [hasMoreMessages, setHasMoreMessages] = useState<boolean>(true);
  const loadMoreMessagesRef = useRef<HTMLDivElement>(null);
  const [typingUsers, setTypingUsers] = useState<string[]>([]);
  const [messageMenuOpen, setMessageMenuOpen] = useState<number | null>(null);
  const [messageMenuAnchor, setMessageMenuAnchor] =
    useState<HTMLElement | null>(null);
  const [mediaUrls, setMediaUrls] = useState<Record<string, string>>({});
  const [loadingMedia, setLoadingMedia] = useState<Record<string, boolean>>({});
  const fetchedUrlsRef = useRef<Set<string>>(new Set());
  const socketRef = useRef<Socket | null>(null);
  const messagesAreaRef = useRef<HTMLDivElement | null>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { userStatuses } = useUserStatuses();
  const location = useLocation();
  const history = useHistory();
  const {
    refetch: refetchCreditsBalance,
    creditsRemaining,
    totalCredits,
  } = useCreditsBalance();
  const { freeMessagesRemaining, freeMessagesLimit } = useFreeMessagesStatus();
  const { hasPremiumSubscription } = useGlobalMonetization();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [, setFileMenuAnchor] = useState<HTMLElement | null>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const acceptedRequestVideoInputRef = useRef<HTMLInputElement>(null);
  const messageInputRef = useRef<HTMLInputElement>(null);
  const [tipDialogVisible, setTipDialogVisible] = useState(false);
  const [recipientProfile, setRecipientProfile] = useState<ProfileData | null>(
    null
  );
  const [requestVideoDialog, setRequestVideoDialog] = useState(false);
  const [videoRequestModalMessage, setVideoRequestModalMessage] =
    useState<Message | null>(null);
  const [videoRequestAgreeTerms, setVideoRequestAgreeTerms] = useState(false);
  const [videoRequestModalPrice, setVideoRequestModalPrice] = useState("");
  const [videoRequestModalRequest, setVideoRequestModalRequest] = useState("");
  const [videoRequestModalFrom, setVideoRequestModalFrom] = useState("");
  const [showRejectedPopup, setShowRejectedPopup] = useState(false);
  const [rejectedVideoRequestId, setRejectedVideoRequestId] = useState<
    string | null
  >(null);
  const [rejectedMessage, setRejectedMessage] = useState<Message | null>(null);
  const [acceptedRequestModalMessage, setAcceptedRequestModalMessage] =
    useState<Message | null>(null);
  const [acceptedRequestFrom, setAcceptedRequestFrom] = useState("");
  const [acceptedRequestRequest, setAcceptedRequestRequest] = useState("");
  const [acceptedRequestSelectedFile, setAcceptedRequestSelectedFile] =
    useState<File | null>(null);
  const [acceptedRequestMessageToViewer, setAcceptedRequestMessageToViewer] =
    useState("");
  const [acceptedRequestPreviewUrl, setAcceptedRequestPreviewUrl] = useState<
    string | null
  >(null);
  const allowedImageExtensions = ["jpg", "jpeg", "png", "webp", "gif"];

  // Preview URL for accepted request video (revoke on cleanup)
  useEffect(() => {
    if (!acceptedRequestSelectedFile) {
      setAcceptedRequestPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(acceptedRequestSelectedFile);
    setAcceptedRequestPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [acceptedRequestSelectedFile]);

  // Keyboard scroll management for mobile
  const { focusAndCenterInput } = useKeyboardScroll({
    enabled: isMobile,
  });
  const allowedVideoExtensions = ["mp4"];
  const allowedTypes = [
    "image/jpeg",
    "image/png",
    "image/webp",
    "image/gif",
    "video/mp4",
  ];
  const [showSidebar, setShowSidebar] = useState<boolean>(true);

  const canShowOnlineStatus = (participant?: ParticipantPresence | null) =>
    participant?.privacySettings?.showOnlineStatus !== false;

  const buildParticipantStatus = (
    participant?: ParticipantPresence | null,
    isOnline?: boolean
  ) => {
    if (!participant || !canShowOnlineStatus(participant)) return "";
    if (isOnline) return "online";
    return participant.lastActive
      ? `last online ${formatTimeAgo(new Date(participant.lastActive))}`
      : "offline";
  };

  const selectedIdRef = useRef<string | null>(null);
  const [openImage, setOpenImage] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const urlSetConversation = useRef(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [searchType, setSearchType] = useState<"users" | "messages">("users");
  const [searchAnchorEl, setSearchAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [globalMessages, setGlobalMessages] = useState<Message[]>([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const emojiButtonRef = useRef<HTMLButtonElement>(null);
  const [cursorPosition, setCursorPosition] = useState<number>(0);
  const [openModal, setOpenModal] = useState(false);
  const [isPaidMedia, setIsPaidMedia] = useState(false);
  const [mediaPrice, setMediaPrice] = useState<number | undefined>(undefined);
  const [mediaDialogVisible, setMediaDialogVisible] = useState(false);
  const [mediaPreviewUrl, setMediaPreviewUrl] = useState<string | null>(null);
  const [mediaType, setMediaType] = useState<"image" | "video" | null>(null);
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);
  const [uploadMediaModalOpen, setUploadMediaModalOpen] = useState(false);
  const [purchaseMediaDialogVisible, setPurchaseMediaDialogVisible] =
    useState(false);
  const [selectedMessageForPurchase, setSelectedMessageForPurchase] =
    useState<Message | null>(null);
  const [headerMenuAnchor, setHeaderMenuAnchor] = useState<HTMLElement | null>(
    null
  ); // State for header menu
  const [muteMenuAnchor, setMuteMenuAnchor] = useState<HTMLElement | null>(
    null
  ); // State for mute submenu
  const [blockModalOpen, setBlockModalOpen] = useState(false); // State for block confirmation modal
  const [blockUserId, setBlockUserId] = useState<string | null>(null); // Store user ID to block
  const [deleteConversationModalOpen, setDeleteConversationModalOpen] =
    useState(false); // State for delete conversation confirmation modal
  const [swipeStartX, setSwipeStartX] = useState<number | null>(null); // State for swipe start position
  const allMediaLoaded = () =>
    Object.values(loadingMedia).every(isLoading => isLoading === false);

  // Функції для роботи з заглушеними користувачами через API
  const [mutedUsers, setMutedUsers] = useState<any[]>([]);

  const checkMuteStatus = async (targetUserId: string) => {
    if (!targetUserId) return;

    try {
      const response = await getMuteStatusAPI(targetUserId);

      // Оновлюємо стан на основі has_muted_target
      if (response.has_muted_target) {
        // Користувач заглушений - додаємо до списку
        setMutedUsers(prev => {
          const exists = prev.some(
            mute => mute.target_user_id === targetUserId
          );
          if (!exists) {
            // Створюємо тимчасовий mute об'єкт для UI
            const tempMute = {
              _id: `${targetUserId}`,
              target_user_id: targetUserId,
              is_active: true,
              expires_at: null, // Не знаємо точну дату, але це не критично для UI
            };
            return [...prev, tempMute];
          }
          return prev;
        });
      } else {
        // Користувач не заглушений - видаляємо зі списку
        setMutedUsers(prev =>
          prev.filter(mute => mute.target_user_id !== targetUserId)
        );
      }
    } catch (error) {
      // Error checking mute status
    }
  };

  const isUserMuted = (userId: string): boolean => {
    return mutedUsers.some(
      mute =>
        mute.target_user_id === userId &&
        mute.is_active &&
        (!mute.expires_at || new Date(mute.expires_at) > new Date())
    );
  };

  const unmuteUser = async (userId: string) => {
    try {
      // Знаходимо mute запис для цього користувача
      const muteRecord = mutedUsers.find(
        mute => mute.target_user_id === userId
      );
      if (!muteRecord) {
        showToast({ message: "Mute record not found", err: true });
        return;
      }

      const response = await unmuteUserAPI(muteRecord._id);
      if (response.message) {
        setMutedUsers(prev =>
          prev.filter(mute => mute.target_user_id !== userId)
        );
        showToast({ message: "User unmuted", success: true });
      } else {
        showToast({
          message: response.error || "Failed to unmute user",
          err: true,
        });
      }
    } catch (error) {
      showToast({ message: "Failed to unmute user", err: true });
    }
  };

  const scrollToBottom = () => {
    if (messagesAreaRef.current) {
      messagesAreaRef.current.scrollTop = messagesAreaRef.current.scrollHeight;
    }
  };

  const fetchMediaUrl = async (
    url: string,
    type: string = "image"
  ): Promise<string> => {
    if (!url || url === "defaultAvatars") {
      return defaultAvatars;
    }
    // Якщо це Google URL (lh3.googleusercontent.com), використовуємо його напряму
    if (url.includes("lh3.googleusercontent.com")) {
      if (fetchedUrlsRef.current.has(url)) {
        return mediaUrls[url] || url;
      }
      setMediaUrls(prev => ({ ...prev, [url]: url }));
      fetchedUrlsRef.current.add(url);
      setLoadingMedia(prev => ({ ...prev, [url]: false }));
      return url;
    }
    // Якщо це пряме посилання (https:// або http://), використовуємо його напряму
    if (url.startsWith("https://") || url.startsWith("http://")) {
      if (fetchedUrlsRef.current.has(url)) {
        return mediaUrls[url] || url;
      }
      setMediaUrls(prev => ({ ...prev, [url]: url }));
      fetchedUrlsRef.current.add(url);
      setLoadingMedia(prev => ({ ...prev, [url]: false }));
      return url;
    }
    if (fetchedUrlsRef.current.has(url)) {
      return mediaUrls[url] || defaultAvatars;
    }
    setLoadingMedia(prev => ({ ...prev, [url]: true }));
    try {
      if (!url.startsWith("/api/files/")) {
        setMediaUrls(prev => ({ ...prev, [url]: url }));
        fetchedUrlsRef.current.add(url);
        return url;
      }
      const fileId = url.split("/api/files/")[1];
      if (!fileId) throw new Error("Invalid file ID");
      const blobUrl = await getFileByIdAPI(fileId);
      setMediaUrls(prev => ({ ...prev, [url]: blobUrl }));
      fetchedUrlsRef.current.add(url);
      return blobUrl;
    } catch (error) {
      showToast({ message: `Failed to load ${type}`, err: true });
      return defaultAvatars;
    } finally {
      setLoadingMedia(prev => ({ ...prev, [url]: false }));
    }
  };

  const isValidFile = (file: File, type: "image" | "video") => {
    const allowedExtensions =
      type === "image" ? allowedImageExtensions : allowedVideoExtensions;
    const fileExtension = file.name.split(".").pop()?.toLowerCase();
    const isValidType =
      allowedTypes.includes(file.type) &&
      fileExtension &&
      allowedExtensions.includes(fileExtension);

    if (!isValidType) {
      showToast(
        `Unsupported file type. Only ${allowedExtensions.join(
          ", "
        )} files are allowed.`,
        "error"
      );
      return false;
    }

    if (!checkFileSize(file)) {
      showToast(
        `File ${file.name} is too large. Max size is ${process.env.REACT_APP_MAX_FILE_SIZE_MB}MB.`,
        "error"
      );
      return false;
    }

    return true;
  };

  useEffect(() => {
    const fetchAllMedia = async () => {
      const mediaToFetch = [
        ...messages
          .filter(
            msg => msg.mediaUrl && !fetchedUrlsRef.current.has(msg.mediaUrl)
          )
          .map(msg => ({ url: msg.mediaUrl!, type: msg.type })),
        ...messages
          .filter(
            msg =>
              msg.sender.profileImg &&
              !fetchedUrlsRef.current.has(msg.sender.profileImg)
          )
          .map(msg => ({ url: msg.sender.profileImg!, type: "avatar" })),
        ...conversations
          .filter(conv => {
            const imageUrl = conv.isGroup
              ? conv.groupImage
              : conv.participant?.profileImg;
            return imageUrl && !fetchedUrlsRef.current.has(imageUrl);
          })
          .map(conv => ({
            url: conv.isGroup
              ? conv.groupImage!
              : conv.participant!.profileImg!,
            type: "avatar",
          })),
        ...globalMessages
          .filter(
            msg => msg.mediaUrl && !fetchedUrlsRef.current.has(msg.mediaUrl)
          )
          .map(msg => ({ url: msg.mediaUrl!, type: msg.type })),
        ...globalMessages
          .filter(
            msg =>
              msg.sender.profileImg &&
              !fetchedUrlsRef.current.has(msg.sender.profileImg)
          )
          .map(msg => ({ url: msg.sender.profileImg!, type: "avatar" })),
      ];
      const uniqueMedia = Array.from(
        new Set(mediaToFetch.map(item => item.url))
      ).map(url => mediaToFetch.find(item => item.url === url)!);
      if (uniqueMedia.length > 0) {
        await Promise.all(
          uniqueMedia.map(async ({ url, type }) => {
            await fetchMediaUrl(url, type);
          })
        );
      }
    };
    fetchAllMedia();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, conversations, globalMessages]);

  useEffect(() => {
    return () => {
      const blobUrls = Object.values(mediaUrls).filter(url =>
        url.startsWith("blob:")
      );
      blobUrls.forEach(url => URL.revokeObjectURL(url));
      setMediaUrls({});
      setLoadingMedia({});
      // eslint-disable-next-line react-hooks/exhaustive-deps
      fetchedUrlsRef.current.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlConversationId = searchParams.get("conversationId");
    if (urlConversationId) {
      selectedIdRef.current = urlConversationId;
      setSelectedConversationId(urlConversationId);
      urlSetConversation.current = true;

      // On mobile, hide sidebar when opening a specific conversation
      if (isMobile) {
        setShowSidebar(false);
      }
    }
  }, [location.search, isMobile]);

  useEffect(() => {
    const fetchConversations = async () => {
      setLoading(true);
      try {
        const response = await getConversationsAPI({
          page: 1,
          perPage: 10,
          search: searchType === "users" ? searchQuery : "",
        });
        if (response.success) {
          const updatedConversations = (response.data.items || []).map(
            (conv: Conversation) => ({
              ...conv,
              lastMessage: conv.lastMessage || {
                content: "",
                senderId: "",
                type: "",
                createdAt: new Date(0).toISOString(), // Use epoch time for conversations without messages
              },
              participant: conv.isGroup
                ? null
                : {
                  ...conv.participant,
                  status: buildParticipantStatus(
                    conv.participant,
                    Boolean(
                      conv.participant?._id && userStatuses[conv.participant._id]
                    )
                  ),
                },
              participants: conv.isGroup
                ? conv.participants.map(p => ({
                  ...p,
                  status: buildParticipantStatus(
                    p,
                    Boolean(userStatuses[p._id])
                  ),
                }))
                : [],
            })
          );
          // Sort conversations by last message time (most recent first)
          const sortedConversations = updatedConversations.sort(
            (a: Conversation, b: Conversation) => {
              const timeA = new Date(a.lastMessage.createdAt).getTime();
              const timeB = new Date(b.lastMessage.createdAt).getTime();

              // Handle invalid dates by treating them as very old (epoch time)
              const validTimeA = isNaN(timeA) ? 0 : timeA;
              const validTimeB = isNaN(timeB) ? 0 : timeB;

              return validTimeB - validTimeA;
            }
          );
          setConversations(sortedConversations);
          if (
            !selectedConversationId &&
            !urlSetConversation.current &&
            sortedConversations.length > 0
          ) {
            setSelectedConversationId(sortedConversations[0]._id);
          }
        } else {
          showToast({
            message: response.message || "Failed to load chats",
            err: true,
          });
        }
      } catch (error) {
        showToast({ message: "Error loading chats", err: true });
      } finally {
        setLoading(false);
      }
    };
    if (userId && searchType === "users") fetchConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, searchQuery, searchType, userStatuses]);

  useEffect(() => {
    if (searchType === "messages" && searchQuery) {
      setLoading(true);
      searchMessagesGloballyAPI({ page: 1, perPage: 10, search: searchQuery })
        .then(response => {
          if (response.success) {
            setGlobalMessages(response.data.items || []);
          } else {
            showToast({
              message: response.message || "Failed to search messages",
              err: true,
            });
          }
        })
        .catch(() => {
          showToast({ message: "Error searching messages", err: true });
        })
        .finally(() => setLoading(false));
    } else {
      setGlobalMessages([]);
    }
  }, [searchQuery, searchType, userId]);

  useEffect(() => {
    if (!userId || !selectedConversationId) return;
    setLoading(true);
    setMessagesPage(1);
    setHasMoreMessages(true);
    const fetchMessages = async () => {
      try {
        const response = await getMessagesAPI(selectedConversationId, {
          page: 1,
          perPage: 20,
        });
        if (response.success) {
          const messagesList = response.data.items || [];

          // Decrypt encrypted messages
          const decryptedMessages = await Promise.all(
            messagesList.map(async (msg: Message) => {
              if (msg.isEncrypted) {
                try {
                  // Use encryptedContent if content is the same (backend bug workaround)
                  const encryptedData =
                    msg.encryptedContent ||
                    (msg.content === msg.encryptedContent
                      ? msg.encryptedContent
                      : msg.content);

                  const decryptedContent = await processEncryptedMessage(
                    {
                      ...msg,
                      encryptedContent: encryptedData,
                    },
                    selectedConversationId
                  );
                  return {
                    ...msg,
                    content: decryptedContent,
                    // Keep encryptedContent for reference but don't display it
                  };
                } catch (error) {
                  // Return message with placeholder if decryption fails
                  return {
                    ...msg,
                    content: "[Encrypted message - unable to decrypt]",
                  };
                }
              }
              return msg;
            })
          );

          setMessages(decryptedMessages.reverse());
          setHasMoreMessages(messagesList.length === 20);
        } else {
          showToast({
            message: response.message || "Failed to load messages",
            err: true,
          });
        }
      } catch (error) {
        showToast({ message: "Error loading messages", err: true });
      } finally {
        setLoading(false);
      }
    };

    const fetchRecipientProfile = async () => {
      const conversation = conversations.find(
        c => c._id === selectedConversationId
      );
      if (!conversation?.isGroup) {
        const recipientId = conversation?.participant?._id;
        if (recipientId) {
          try {
            const data = await getUserProfile(recipientId);
            setRecipientProfile({
              _id: data._id,
              firstName: data.firstName,
              lastName: data.lastName,
              username: data.username,
              privacySettings:
                data?.privacySettings || data?.details?.privacySettings || {},
              suggestedTips:
                Array.isArray(data.suggestedTips) && data.suggestedTips.length
                  ? data.suggestedTips
                  : [5, 10, 15, 20, 25],
              minimumTipAmount: data.minimumTipAmount || 2,
              customContentPrice: data.customContentPrice || 100,
            });
          } catch (err) {
            showToast("Error fetching recipient profile", "error");
          }
        }
      } else {
        setRecipientProfile(null);
      }
    };
    fetchMessages();
    fetchRecipientProfile();
  }, [selectedConversationId, userId, conversations]);

  // Function to load more messages (older messages)
  const loadMoreMessages = async () => {
    if (!selectedConversationId || loadingMore || !hasMoreMessages) return;

    setLoadingMore(true);
    const previousScrollHeight = messagesAreaRef.current?.scrollHeight || 0;

    try {
      const nextPage = messagesPage + 1;
      const response = await getMessagesAPI(selectedConversationId, {
        page: nextPage,
        perPage: 20,
      });

      if (response.success) {
        const messagesList = response.data.items || [];

        if (messagesList.length === 0) {
          setHasMoreMessages(false);
          setLoadingMore(false);
          return;
        }

        // Decrypt encrypted messages
        const decryptedMessages = await Promise.all(
          messagesList.map(async (msg: Message) => {
            if (msg.isEncrypted) {
              try {
                const encryptedData =
                  msg.encryptedContent ||
                  (msg.content === msg.encryptedContent
                    ? msg.encryptedContent
                    : msg.content);

                const decryptedContent = await processEncryptedMessage(
                  {
                    ...msg,
                    encryptedContent: encryptedData,
                  },
                  selectedConversationId
                );
                return {
                  ...msg,
                  content: decryptedContent,
                };
              } catch (error) {
                return {
                  ...msg,
                  content: "[Encrypted message - unable to decrypt]",
                };
              }
            }
            return msg;
          })
        );

        // Add older messages to the beginning of the list
        setMessages(prev => [...decryptedMessages.reverse(), ...prev]);
        setMessagesPage(nextPage);
        setHasMoreMessages(messagesList.length === 20);

        // Maintain scroll position after loading
        requestAnimationFrame(() => {
          if (messagesAreaRef.current) {
            const newScrollHeight = messagesAreaRef.current.scrollHeight;
            const scrollDifference = newScrollHeight - previousScrollHeight;
            messagesAreaRef.current.scrollTop += scrollDifference;
          }
        });
      }
    } catch (error) {
      showToast({ message: "Error loading more messages", err: true });
    } finally {
      setLoadingMore(false);
    }
  };

  // Infinite scroll observer for loading older messages
  useEffect(() => {
    if (!hasMoreMessages || loadingMore || !loadMoreMessagesRef.current) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMoreMessages && !loadingMore) {
          loadMoreMessages();
        }
      },
      { threshold: 0.1 }
    );

    const currentRef = loadMoreMessagesRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMoreMessages, loadingMore, messagesPage, selectedConversationId]);

  useEffect(() => {
    if (messages.length > 0 && !loadingMore) {
      requestAnimationFrame(() => {
        scrollToBottom();
      });
    }
  }, [messages.length, loadingMore]);

  useEffect(() => {
    if (messages.length === 0) return;

    if (allMediaLoaded()) {
      requestAnimationFrame(() => {
        scrollToBottom();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingMedia]);

  // Initialize E2EE on component mount (for private conversations)
  useEffect(() => {
    if (!userId) return;

    // Initialize E2EE keys if not already initialized
    // This should happen automatically when user first uses E2EE
    initializeE2EE().catch(() => {
      // Silently fail - E2EE is optional
    });
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    const socket = getSocket();
    if (!socket) {
      return;
    }
    socketRef.current = socket;
    if (selectedConversationId) {
      socket.emit("joinConversation", selectedConversationId);
    }
    socket.on(
      "newMessage",
      async ({
        conversationId,
        message,
      }: {
        conversationId: string;
        message: Message;
      }) => {
        // Decrypt message if it's encrypted
        let processedMessage = message;
        if (message.isEncrypted) {
          try {
            // Use encryptedContent if content is the same (backend bug workaround)
            const encryptedData =
              message.encryptedContent ||
              (message.content === message.encryptedContent
                ? message.encryptedContent
                : message.content);

            const decryptedContent = await processEncryptedMessage(
              {
                ...message,
                encryptedContent: encryptedData,
              },
              conversationId
            );
            processedMessage = {
              ...message,
              content: decryptedContent,
              // Keep encryptedContent for reference but don't display it
            };
          } catch (error) {
            // Return message with placeholder if decryption fails
            processedMessage = {
              ...message,
              content: "[Encrypted message - unable to decrypt]",
            };
          }
        }

        if (conversationId === selectedConversationId) {
          setMessages(prev => [...prev, processedMessage]);
          scrollToBottom();
          if (processedMessage.senderId !== userId) {
            updateMessageStatusAPI(processedMessage._id, "read").catch(
              () => { }
            );
            if (processedMessage.sender.profileImg)
              fetchMediaUrl(processedMessage.sender.profileImg, "avatar");
            if (processedMessage.mediaUrl)
              fetchMediaUrl(processedMessage.mediaUrl, processedMessage.type);
          }
        }
        setConversations(prev => {
          const updated = [...prev];
          const index = updated.findIndex(c => c._id === conversationId);
          if (index !== -1) {
            updated[index] = {
              ...updated[index],
              lastMessage: {
                content: processedMessage.content,
                senderId: processedMessage.senderId,
                type: processedMessage.type,
                createdAt: processedMessage.createdAt,
              },
              unreadCount:
                processedMessage.senderId === userId
                  ? updated[index].unreadCount
                  : updated[index].unreadCount + 1,
            };
            return [...updated].sort((a, b) => {
              const timeA = new Date(a.lastMessage.createdAt).getTime();
              const timeB = new Date(b.lastMessage.createdAt).getTime();

              // Handle invalid dates by treating them as very old (epoch time)
              const validTimeA = isNaN(timeA) ? 0 : timeA;
              const validTimeB = isNaN(timeB) ? 0 : timeB;

              return validTimeB - validTimeA;
            });
          }
          return updated;
        });
      }
    );

    socket.on(
      "messageStatusUpdate",
      ({ messageId, status }: MessageStatusUpdate) => {
        setMessages(prev =>
          prev.map(msg => (msg._id === messageId ? { ...msg, status } : msg))
        );
      }
    );

    socket.on(
      "messageDeleted",
      ({ messageId, conversationId }: MessageDeleted) => {
        if (conversationId === selectedConversationId) {
          setMessages(prev => prev.filter(msg => msg._id !== messageId));
        }
      }
    );

    socket.on("typing", ({ userId: typingUserId }: TypingPayload) => {
      if (typingUserId !== userId) {
        setTypingUsers(prev =>
          prev.includes(typingUserId) ? prev : [...prev, typingUserId]
        );
      }
    });

    socket.on("stopTyping", ({ userId: typingUserId }: TypingPayload) => {
      setTypingUsers(prev => prev.filter(id => id !== typingUserId));
    });

    socket.on(
      "userStatusUpdate",
      ({ userId: updatedUserId, status }: UserStatusUpdatePayload) => {
        setConversations(prev =>
          prev.map(conv => {
            if (conv.isGroup) {
              return {
                ...conv,
                participants: conv.participants.map(p =>
                  p._id === updatedUserId
                    ? {
                      ...p,
                      status: buildParticipantStatus(p, status === "online"),
                    }
                    : p
                ),
              };
            } else {
              return conv.participant?._id === updatedUserId
                ? {
                  ...conv,
                  participant: {
                    ...conv.participant,
                    status: buildParticipantStatus(
                      conv.participant,
                      status === "online"
                    ),
                  },
                }
                : conv;
            }
          })
        );
      }
    );

    socket.on(
      "videoRequestStatusUpdate",
      ({ messageId, status }: { messageId: string; status: string }) => {
        setMessages(prev =>
          prev.map(msg =>
            msg._id === messageId
              ? {
                ...msg,
                videoRequest: msg.videoRequest
                  ? { ...msg.videoRequest, status: status as any }
                  : undefined,
              }
              : msg
          )
        );
      }
    );

    socket.on("connect_error", (error: Error) => {
      showToast({ message: "Error connecting to chat", err: true });
    });

    socket.on(
      "conversationDeleted",
      ({ conversationId }: { conversationId: string }) => {
        // Remove conversation from list
        setConversations(prev =>
          prev.filter(conv => conv._id !== conversationId)
        );

        // If deleted conversation was selected, clear it
        if (selectedConversationId === conversationId) {
          setSelectedConversationId(null);
          setMessages([]);
          history.push("/messages");
        }
      }
    );

    return () => {
      socket.off("newMessage");
      socket.off("messageStatusUpdate");
      socket.off("messageDeleted");
      socket.off("typing");
      socket.off("stopTyping");
      socket.off("userStatusUpdate");
      socket.off("videoRequestStatusUpdate");
      socket.off("conversationDeleted");
      socket.off("connect_error");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, selectedConversationId]);

  const handleChatSelect = (conversationId: string) => {
    selectedIdRef.current = conversationId;
    setSelectedConversationId(conversationId);
    setSearchQuery("");
    if (isMobile) setShowSidebar(false);
    if (socketRef.current)
      socketRef.current.emit("joinConversation", conversationId);
    history.push(`/messages?conversationId=${conversationId}`);
  };

  const handleBackToChats = () => {
    setShowSidebar(true);
    setSearchQuery("");
    history.push("/messages");
  };

  const handleSendMessage = async () => {
    if (!selectedConversationId) return;
    if (isPaidMedia && (mediaPrice === undefined || mediaPrice <= 0)) {
      showToast({
        message: "Please enter a valid price for paid content",
        err: true,
      });
      return;
    }

    // Check if user is Creator or Viewer
    const userTypeId = user?.userType?._id;
    const userTypeTitle = user?.userType?.title?.toLowerCase();
    const isCreator =
      userTypeTitle === "creator" || userTypeId === "6800a63485ec077c7c266c25";
    const isViewer =
      userTypeTitle === "viewer" || userTypeId === "6800a68085ec077c7c266c27";

    // For Viewers: Check if they have 0 credits before sending message
    // Creators can send and receive messages for free, so skip this check for them
    if (
      isViewer &&
      !isCreator &&
      typeof totalCredits === "number" &&
      totalCredits === 0
    ) {
      // Show upgrade modal when Viewer tries to send message with 0 credits
      setShowUpgradeModal(true);
      return;
    }

    const currentConversation = conversations.find(
      c => c._id === selectedConversationId
    );
    const isGroup = currentConversation?.isGroup;
    const recipientId = !isGroup
      ? currentConversation?.participant?._id
      : undefined;

    // Privacy: when enabled by recipient, messaging requires an active subscription.
    if (
      !isGroup &&
      recipientId &&
      recipientProfile?.privacySettings?.allowMessagesWithoutSubscription ===
      true
    ) {
      try {
        const subRes = await checkUserSubscriptionAPI(recipientId);
        const hasSubscription = Boolean(
          subRes?.isActive ||
          subRes?.isSubscribed ||
          subRes?.data?.isActive ||
          subRes?.data?.isSubscribed
        );

        if (!hasSubscription) {
          showToast({
            message:
              "An active subscription is required to send messages to this user.",
            err: true,
          });
          return;
        }
      } catch {
        showToast({
          message:
            "Unable to verify subscription status. Please try again in a moment.",
          err: true,
        });
        return;
      }
    }

    // Ensure E2EE session exists for private conversations (not groups)
    if (!isGroup && recipientId) {
      try {
        await ensureSessionExists(recipientId, selectedConversationId);
      } catch (error) {
        // Continue with message send even if E2EE setup fails
      }
    }

    try {
      const response = await sendMessageAPI(
        {
          conversationId: selectedConversationId,
          recipientId,
          content: inputValue.trim(),
          type: mediaType || "text",
          isPaid: isPaidMedia,
          price: isPaidMedia ? mediaPrice || 0 : 0,
        },
        selectedFiles.length > 0 ? selectedFiles : []
      );
      if (response.success) {
        const role = user?.userType?.title?.toLowerCase?.() || "guest";
        trackMessageSent({
          user_id: user?.id,
          user_role:
            role === "viewer" || role === "creator" ? role : "guest",
          is_paid_message: Boolean(isPaidMedia),
        });

        setInputValue("");
        setSelectedFiles([]);
        setMediaPreviewUrl(null);
        setIsPaidMedia(false);
        setMediaPrice(undefined);
        setMediaType(null);
        scrollToBottom();

        // Refetch credits balance after successful message send
        refetchCreditsBalance();

        // Update conversation list to move current conversation to top
        setConversations(prev => {
          const updated = [...prev];
          const index = updated.findIndex(
            c => c._id === selectedConversationId
          );
          if (index !== -1) {
            const currentConv = updated[index];
            updated[index] = {
              ...currentConv,
              lastMessage: {
                content: inputValue.trim(),
                senderId: userId || "",
                type: mediaType || "text",
                createdAt: new Date().toISOString(),
              },
            };
            // Move to top and sort by last message time
            const movedConv = updated.splice(index, 1)[0];
            updated.unshift(movedConv);
            return updated.sort((a, b) => {
              const timeA = new Date(a.lastMessage.createdAt).getTime();
              const timeB = new Date(b.lastMessage.createdAt).getTime();

              // Handle invalid dates by treating them as very old (epoch time)
              const validTimeA = isNaN(timeA) ? 0 : timeA;
              const validTimeB = isNaN(timeB) ? 0 : timeB;

              return validTimeB - validTimeA;
            });
          }
          return updated;
        });

        // Center input above keyboard on mobile after sending message
        if (isMobile && messageInputRef.current) {
          // Для iOS потрібна більша затримка
          const isIOS =
            /iPad|iPhone|iPod/.test(navigator.userAgent) ||
            (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
          const delay = isIOS ? 600 : 400;

          setTimeout(() => {
            focusAndCenterInput(messageInputRef);
          }, delay);
        }
      } else {
        // Show UpgradeModal when API returns insufficient credits
        if (
          response?.message === "Insufficient credits to send message" ||
          (response?.success === false && response?.available === 0)
        ) {
          setShowUpgradeModal(true);
        } else {
          showToast({
            message: response.message || "Unable to send a message",
            err: true,
          });
        }
      }
    } catch (error: any) {
      // Handle insufficient credits response (API may reject with response body)
      const errorData = error?.response?.data ?? error;
      const isInsufficientCredits =
        errorData?.success === false &&
        (errorData?.message === "Insufficient credits to send message" ||
          errorData?.available === 0);

      if (isInsufficientCredits) {
        setShowUpgradeModal(true);
        return;
      }

      // Handle other API errors
      let errorMessage = "Помилка при відправці повідомлення";
      if (error?.response?.data) {
        const errData = error.response.data;
        if (errData.message) {
          errorMessage = errData.message;
        } else if (errData.error) {
          errorMessage = errData.error;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showToast(errorMessage, "error");
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    // Save cursor position
    if (messageInputRef.current) {
      setCursorPosition(messageInputRef.current.selectionStart || 0);
    }
    if (socketRef.current && selectedConversationId) {
      socketRef.current.emit("typing", {
        conversationId: selectedConversationId,
        userId,
      });
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        socketRef.current?.emit("stopTyping", {
          conversationId: selectedConversationId,
          userId,
        });
      }, 2000);
    }
  };

  const handleFileMenuClose = () => {
    setFileMenuAnchor(null);
  };

  const handleFileUpload = (
    event: React.ChangeEvent<HTMLInputElement>,
    type: "image" | "video"
  ) => {
    const file = event.target.files?.[0];
    if (!file || !isValidFile(file, type)) {
      setSelectedFiles([]);
      return;
    }
    setSelectedFiles([file]);
    setMediaPreviewUrl(URL.createObjectURL(file));
    setMediaType(type);
    setIsPaidMedia(false);
    setMediaPrice(undefined);
    setMediaDialogVisible(true);
    handleFileMenuClose();
  };

  const handleTipSuccess = async (tipAmount: number) => {
    try {
      // This function is called after successful payment
      // Create the tip message manually since backend might not be creating it automatically

      if (selectedConversationId && recipientProfile?._id) {
        const tipMessage = {
          conversationId: selectedConversationId,
          recipientId: recipientProfile._id,
          content: `Sent a $${tipAmount} tip`,
          type: "tip",
          isPaid: false,
          price: 0,
          tipAmount: tipAmount,
        };

        const response = await sendMessageAPI(tipMessage, []);

        if (response.success) {
          // Add the tip message to the messages list
          const newTipMessage: Message = {
            _id: response.message?._id || `tip-${Date.now()}`,
            senderId: userId || "",
            sender: {
              _id: userId || "",
              username: user?.username,
              profileImg: user?.profileImg,
            },
            conversationId: selectedConversationId,
            content: `Sent a $${tipAmount} tip`,
            type: "tip",
            status: "sent",
            createdAt: new Date().toISOString(),
            conversation: {
              _id: selectedConversationId,
              participants: [],
            },
            isPaid: false,
            price: 0,
            tipAmount: tipAmount,
          };

          setMessages(prev => [...prev, newTipMessage]);
          scrollToBottom();

          // Update conversation list to move current conversation to top
          setConversations(prev => {
            const updated = [...prev];
            const index = updated.findIndex(
              c => c._id === selectedConversationId
            );
            if (index !== -1) {
              const currentConv = updated[index];
              updated[index] = {
                ...currentConv,
                lastMessage: {
                  content: `Sent a $${tipAmount} tip`,
                  senderId: userId || "",
                  type: "tip",
                  createdAt: new Date().toISOString(),
                },
              };
              // Move to top and sort by last message time
              const movedConv = updated.splice(index, 1)[0];
              updated.unshift(movedConv);
              return updated.sort((a, b) => {
                const timeA = new Date(a.lastMessage.createdAt).getTime();
                const timeB = new Date(b.lastMessage.createdAt).getTime();
                const validTimeA = isNaN(timeA) ? 0 : timeA;
                const validTimeB = isNaN(timeB) ? 0 : timeB;
                return validTimeB - validTimeA;
              });
            }
            return updated;
          });
        } else {
          showToast(
            "Tip payment successful, but failed to create message.",
            "error"
          );
        }
      }

      showToast(
        `Tipped $${tipAmount} to ${recipientProfile?.firstName} ${recipientProfile?.lastName}!`,
        "success"
      );
      setTipDialogVisible(false);
    } catch (err) {
      showToast("Error processing tip.", "error");
    }
  };

  const handlePurchaseMedia = (message: Message) => {
    if (!userId) {
      showToast("You must be logged in to purchase content.", "error");
      return;
    }
    setSelectedMessageForPurchase(message);
    setPurchaseMediaDialogVisible(true);
  };

  const handleCompletePurchase = () => {
    if (!selectedMessageForPurchase) {
      showToast("No post selected for purchase.", "error");
      return;
    }
    setPurchaseMediaDialogVisible(false);
  };

  const handleAcceptVideoRequest = async (message: Message) => {
    if (!message.videoRequest?._id) {
      showToast("Invalid video request.", "error");
      return;
    }

    try {
      // Call API to accept video request
      await acceptVideoRequestAPI(message.videoRequest?._id);

      // Update message status locally
      setMessages(prev =>
        prev.map(msg =>
          msg._id === message._id
            ? {
              ...msg,
              videoRequest: {
                ...msg.videoRequest!,
                status: "accepted",
              },
            }
            : msg
        )
      );

      showToast(
        "Video request accepted! You can now upload the video.",
        "success"
      );

      // Send message to chat (creator's side)
      /*if (selectedConversationId) {
        const chatMessage = {
          conversationId: selectedConversationId,
          recipientId: message.senderId,
          content: "✓ You accepted custom video request",
          type: "text",
          isPaid: false,
          price: 0,
        };
        await sendMessageAPI(chatMessage, []);
      }*/
    } catch (error) {
      showToast("Failed to accept video request.", "error");
    }
  };

  const handleRejectVideoRequest = async (message: Message) => {
    if (!message.videoRequest?._id) {
      showToast("Invalid video reques_t.", "error");
      return;
    }

    try {
      // Call API to reject video request
      await rejectVideoRequestAPI(message.videoRequest?._id);

      // Update message status locally
      setMessages(prev =>
        prev.map(msg =>
          msg._id === message._id
            ? {
              ...msg,
              videoRequest: {
                ...msg.videoRequest!,
                status: "rejected",
              },
            }
            : msg
        )
      );

      showToast("Video request rejected.", "success");

      // Send notification message to requester
      if (selectedConversationId) {
        const notificationMessage = {
          conversationId: selectedConversationId,
          recipientId: message.senderId,
          content: "✕ You rejected custom video request",
          type: "text",
          isPaid: false,
          price: 0,
        };
        await sendMessageAPI(notificationMessage, []);
      }
    } catch (error) {
      showToast("Failed to reject video request.", "error");
    }
  };

  const handleCancelVideoRequest = async (message: Message) => {
    if (!message.videoRequest?._id) {
      showToast("Invalid video request.", "error");
      return;
    }

    try {
      // Call API to cancel video request
      await cancelVideoRequestAPI(message.videoRequest?._id);

      // Update message status locally
      setMessages(prev =>
        prev.map(msg =>
          msg._id === message._id
            ? {
              ...msg,
              videoRequest: {
                ...msg.videoRequest!,
                status: "cancelled",
              },
            }
            : msg
        )
      );

      showToast("Video request cancelled.", "success");

      // Send notification message to creator
      if (selectedConversationId) {
        const notificationMessage = {
          conversationId: selectedConversationId,
          recipientId: message.senderId,
          content: `🚫 The custom video request has been cancelled by the requester.`,
          type: "notification",
          isPaid: false,
          price: 0,
        };
        await sendMessageAPI(notificationMessage, []);
      }
    } catch (error) {
      showToast("Failed to cancel video request.", "error");
    }
  };

  const handleUploadVideoForRequest = async (
    message: Message,
    videoFile: File
  ) => {
    if (!message.videoRequest?._id) {
      showToast("Invalid video request.", "error");
      return;
    }

    try {
      // Use the dedicated video upload endpoint
      const uploadResponse = await uploadCompletedVideoAPI(
        message.videoRequest?._id,
        videoFile
      );

      if (!uploadResponse.success) {
        // Check for error field first (e.g., "File size exceeds maximum limit of 10MB")
        const errorMessage =
          uploadResponse.error ||
          uploadResponse.message ||
          "Failed to upload video";
        throw new Error(errorMessage);
      }

      // Update message status locally
      setMessages(prev =>
        prev.map(msg =>
          msg._id === message._id
            ? {
              ...msg,
              videoRequest: {
                ...msg.videoRequest!,
                status: "completed",
                videoUrl: uploadResponse.data.videoUrl,
              },
            }
            : msg
        )
      );

      showToast("Video uploaded successfully!", "success");

      // Send notification message to requester
      // if (selectedConversationId) {
      //   const notificationMessage = {
      //     conversationId: selectedConversationId,
      //     recipientId: message.senderId,
      //     content: `🎉 Your custom video is ready! Check the video above.`,
      //     type: "notification",
      //     isPaid: false,
      //     price: 0,
      //   };
      //   await sendMessageAPI(notificationMessage, []);
      // }
    } catch (error: any) {
      // Extract error message from API response
      let errorMessage = "Failed to upload video.";

      if (error?.response?.data) {
        const errorData = error.response.data;
        // Check for error field first (e.g., "File size exceeds maximum limit of 10MB")
        if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showToast(errorMessage, "error");
    }
  };

  const handleMessageMenuToggle = (
    event: React.MouseEvent<HTMLElement>,
    messageIndex: number
  ) => {
    event.stopPropagation();
    setMessageMenuAnchor(event.currentTarget);
    setMessageMenuOpen(messageIndex);
  };

  const handleMessageMenuClose = () => {
    setMessageMenuOpen(null);
    setMessageMenuAnchor(null);
  };

  const handleMessageAction = async (action: string, messageIndex: number) => {
    const message = messages[messageIndex];
    if (action === "delete" && message) {
      try {
        const response = await deleteMessageAPI(message._id);
        if (response.success) {
          showToast({ message: "Message deleted", success: true });
          setMessages(prev => prev.filter(msg => msg._id !== message._id));
        } else {
          showToast({
            message: response.message || "Cannot delete a message",
            err: true,
          });
        }
      } catch (error) {
        showToast({ message: "Cannot delete a message", err: true });
      }
    }
    handleMessageMenuClose();
  };

  const handleMuteUser = async (duration: string) => {
    if (!currentConversation?.participant?._id) {
      showToast({ message: "User not found", err: true });
      return;
    }

    // Мапінг UI значень на хвилини для бекенду
    const durationMap: { [key: string]: number } = {
      "1 hour": 60,
      "1 day": 1440,
      "1 week": 10080,
      "1 month": 43200, // 30 днів
    };

    const backendDuration = durationMap[duration] || duration;

    try {
      const response = await muteUserAPI(
        currentConversation.participant._id,
        backendDuration
      );

      if (response.message) {
        // Оновлюємо локальний стан
        setMutedUsers(prev => [...prev, response.mute]);
        showToast({
          message: `User muted for ${duration}`,
          success: true,
        });
      } else {
        showToast({
          message: response.error || "Failed to mute user",
          err: true,
        });
      }
    } catch (error) {
      showToast({
        message: "Failed to mute user",
        err: true,
      });
    }

    setMuteMenuAnchor(null);
    setHeaderMenuAnchor(null);
  };

  const handleDeleteConversation = async (conversationId?: string) => {
    const idToDelete = conversationId || selectedConversationId;
    if (!idToDelete) {
      showToast({ message: "No conversation selected", err: true });
      return;
    }

    try {
      const response = await deleteConversationAPI(idToDelete);
      if (response.success || response.message) {
        showToast({
          message: "Conversation deleted successfully",
          success: true,
        });

        // Remove conversation from list
        setConversations(prev => prev.filter(conv => conv._id !== idToDelete));

        // Clear selected conversation if it was the deleted one
        if (selectedConversationId === idToDelete) {
          setSelectedConversationId(null);
          setMessages([]);
        }

        // Redirect to messages page if on mobile
        if (isMobile) {
          history.push("/messages");
        }

        // Refresh conversations list
        try {
          const conversationsResponse = await getConversationsAPI();
          if (conversationsResponse?.items) {
            setConversations(conversationsResponse.items);
          }
        } catch (error) {
          // Error refreshing conversations
        }
      } else {
        showToast({
          message: response.message || "Failed to delete conversation",
          err: true,
        });
      }
    } catch (error: any) {
      showToast({
        message: error?.message || "Failed to delete conversation",
        err: true,
      });
    } finally {
      setDeleteConversationModalOpen(false);
      setHeaderMenuAnchor(null);
    }
  };

  // Swipe handlers for mobile delete
  const handleTouchStart = (e: React.TouchEvent, conversationId: string) => {
    if (!isMobile) return;
    const touch = e.touches[0];
    setSwipeStartX(touch.clientX);
  };

  const handleTouchEnd = (e: React.TouchEvent, conversationId: string) => {
    if (!isMobile || swipeStartX === null) return;
    const touch = e.changedTouches[0];
    const finalDiff = swipeStartX - touch.clientX;

    // If swiped more than 60px to the left, show delete modal
    if (finalDiff > 60) {
      setSelectedConversationId(conversationId);
      setDeleteConversationModalOpen(true);
    }

    setSwipeStartX(null);
  };

  const formatTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatTimeLastOnline = (dateString: string | undefined): string => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";
    const hours = date.getHours();
    const mins = date.getMinutes();
    const ampm = hours >= 12 ? "PM" : "AM";
    const h12 = hours % 12 || 12;
    return `${h12}.${mins.toString().padStart(2, "0")} ${ampm}`;
  };

  const formatConversationTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffDays = Math.floor(diffMs / 86400000);

    // If it's today, show time
    if (date.toDateString() === now.toDateString()) {
      if (diffMins < 1) return "now";
      if (diffMins < 60) return `${diffMins}m`;
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    }

    // If it's yesterday
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) {
      return "yesterday";
    }

    // If it's within the last week
    if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: "short" });
    }

    // If it's within the last year
    if (diffDays < 365) {
      return date.toLocaleDateString([], { month: "short", day: "numeric" });
    }

    // For older messages, show full date
    return date.toLocaleDateString([], {
      month: "short",
      day: "numeric",
      year: "2-digit",
    });
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return date.toLocaleDateString("en-US", options);
  };

  const formatDateSeparator = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const day = date.getDate();
    const month = date.toLocaleDateString("en-US", { month: "short" });
    if (date.getFullYear() !== now.getFullYear()) {
      const year = date.getFullYear().toString().slice(-2);
      return `${day} ${month} ${year}`;
    }
    return `${day} ${month}`;
  };

  const shouldShowDate = (
    currentMessage: Message,
    previousMessage: Message | undefined,
    messageIndex: number
  ): boolean => {
    // Завжди показуємо дату для першого повідомлення
    if (!previousMessage) {
      return true;
    }

    const currentDate = new Date(currentMessage.createdAt);
    const previousDate = new Date(previousMessage.createdAt);

    // Показуємо дату якщо повідомлення з різних днів
    const differentDay =
      currentDate.toDateString() !== previousDate.toDateString();

    // Також показуємо дату кожні 10 повідомлень для кращої навігації
    const shouldShowForNavigation = messageIndex % 10 === 0;

    return differentDay || shouldShowForNavigation;
  };

  const formatTimeAgo = (dateInput: Date | string | undefined): string => {
    if (!dateInput) return "never";
    const date =
      typeof dateInput === "string" ? new Date(dateInput) : dateInput;
    if (isNaN(date.getTime())) return "never";

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    return `${diffDays} days ago`;
  };

  useEffect(() => {
    if (isMobile) setShowSidebar(true);
    else setShowSidebar(true);
  }, [isMobile]);

  // Handle clicking outside emoji picker to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      // Don't close if clicking on the emoji button or inside the picker
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(target) &&
        emojiButtonRef.current &&
        !emojiButtonRef.current.contains(target)
      ) {
        setShowEmojiPicker(false);
      }
    };

    if (showEmojiPicker) {
      // Use setTimeout to avoid immediate closure when opening
      setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutside);
      }, 100);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showEmojiPicker]);

  const currentConversation = conversations.find(
    c => c._id === selectedConversationId
  );

  // Перевіряємо статус mute при відкритті чату
  useEffect(() => {
    if (
      currentConversation &&
      !currentConversation.isGroup &&
      currentConversation.participant?._id
    ) {
      checkMuteStatus(currentConversation.participant._id);
    }
  }, [currentConversation]);

  const handleImageClick = async (rawMediaUrl: string) => {
    let fullSizeUrl = mediaUrls[rawMediaUrl];
    if (!fullSizeUrl) fullSizeUrl = await fetchMediaUrl(rawMediaUrl);
    setImageUrl(fullSizeUrl || defaultAvatars);
    setOpenImage(true);
  };

  const handleCloseImage = () => setOpenImage(false);

  const refreshConversations = async () => {
    try {
      const response = await getConversationsAPI({
        page: 1,
        perPage: 10,
        search: searchType === "users" ? searchQuery : "",
      });
      if (response.success) {
        const updatedConversations = (response.data.items || []).map(
          (conv: Conversation) => ({
            ...conv,
            lastMessage: conv.lastMessage || {
              content: "",
              senderId: "",
              type: "",
              createdAt: new Date(0).toISOString(),
            },
            participant: conv.isGroup
              ? null
              : {
                ...conv.participant,
                status: buildParticipantStatus(
                  conv.participant,
                  Boolean(
                    conv.participant?._id && userStatuses[conv.participant._id]
                  )
                ),
              },
            participants: conv.isGroup
              ? conv.participants.map(p => ({
                ...p,
                status: buildParticipantStatus(
                  p,
                  Boolean(userStatuses[p._id])
                ),
              }))
              : [],
          })
        );
        // Sort conversations by last message time (most recent first)
        const sortedConversations = updatedConversations.sort(
          (a: Conversation, b: Conversation) => {
            const timeA = new Date(a.lastMessage.createdAt).getTime();
            const timeB = new Date(b.lastMessage.createdAt).getTime();

            // Handle invalid dates by treating them as very old (epoch time)
            const validTimeA = isNaN(timeA) ? 0 : timeA;
            const validTimeB = isNaN(timeB) ? 0 : timeB;

            return validTimeB - validTimeA;
          }
        );
        setConversations(sortedConversations);
      }
    } catch (error) {
      // Error refreshing conversations
    }
  };

  const refreshAfterBlockUser = () => {
    // Optionally, update UI (e.g., remove conversation or disable input)
    setConversations(prev =>
      prev.filter(conv => conv.participant?._id !== blockUserId)
    );
    if (selectedConversationId === currentConversation?._id) {
      setSelectedConversationId(null);
      history.push("/messages");
    }
  };

  const handleCreateGroup = async ({
    groupName,
    participantIds,
  }: {
    groupName: any;
    participantIds: string[];
  }) => {
    try {
      const res = await createGroupConversationAPI({
        groupName,
        participantIds,
      });
      if (res.data.success) {
        setOpenModal(false);
        showToast({
          message: "Group chat created successfully",
          success: true,
        });

        // Refresh conversations list from API
        await refreshConversations();
      } else {
        showToast({
          message: res.data.message || "Error creating group chat",
          err: true,
        });
        // Refresh conversations list even on failure
        await refreshConversations();
      }
    } catch (err) {
      showToast({ message: "Error creating group chat", err: true });
      // Refresh conversations list even on error
      await refreshConversations();
    }
  };

  const renderMessageContent = (msg: Message, isOutgoing: boolean) => {
    const mediaUrl = msg.mediaUrl;
    const isPaidContent = msg.isPaid && userId && !msg.paidBy?.includes(userId);
    const blurInboxPhotosEnabled = Boolean(
      (user as any)?.privacySettings?.blurInboxPhotos === true ||
      (user as any)?.details?.privacySettings?.blurInboxPhotos === true
    );
    const shouldBlurIncomingMedia = !isOutgoing && blurInboxPhotosEnabled;

    if (msg.type === "image") {
      if (loadingMedia[mediaUrl || ""]) {
        return <CircularProgress size={24} />;
      }
      return (
        <Box>
          <img
            src={mediaUrls[mediaUrl || ""] || defaultAvatars}
            alt="Message content"
            style={{
              maxWidth: "100%",
              maxHeight: "200px",
              filter: shouldBlurIncomingMedia ? "blur(16px)" : "none",
              cursor:
                isPaidContent || shouldBlurIncomingMedia ? "default" : "pointer",
            }}
            onClick={() =>
              !isPaidContent &&
              !shouldBlurIncomingMedia &&
              handleImageClick(msg.mediaUrl || "")
            }
            {...(!hasPremiumSubscription && {
              onContextMenu: (e: React.MouseEvent) => e.preventDefault(),
              onDragStart: (e: React.DragEvent) => e.preventDefault(),
              draggable: false,
            })}
          />
          {isPaidContent && (
            <Button
              className={classes.purchaseButton}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                handlePurchaseMedia(msg);
              }}
            >
              {msg.price} $ Purchase Image
            </Button>
          )}
        </Box>
      );
    } else if (msg.type === "video") {
      if (loadingMedia[mediaUrl || ""]) {
        return <CircularProgress size={24} />;
      }
      return (
        <Box position="relative">
          <video
            style={{
              maxWidth: "100%",
              maxHeight: "200px",
              filter: shouldBlurIncomingMedia ? "blur(16px)" : "none",
            }}
            controls={!isPaidContent && !shouldBlurIncomingMedia}
            controlsList="nodownload nofullscreen noremoteplayback"
            disablePictureInPicture
            onContextMenu={
              !hasPremiumSubscription
                ? (e: React.MouseEvent) => e.preventDefault()
                : undefined
            }
          >
            <source
              src={mediaUrls[mediaUrl || ""] || mediaUrl || ""}
              type="video/mp4"
            />
            Your browser does not support the video tag.
          </video>
          {isPaidContent && (
            <Button
              className={classes.purchaseButton}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                handlePurchaseMedia(msg);
              }}
            >
              {msg.price} $ Purchase this Video
            </Button>
          )}
        </Box>
      );
    } else if (msg.type === "video_request") {
      const label = isOutgoing
        ? "You've sent a custom video request"
        : "You've received a custom video request";
      return (
        <Box className={classes.videoRequestContainer}>
          <Box className={classes.videoRequestCardCompact}>
            <Box className={classes.videoRequestCardRow}>
              <img
                src={VideoRequestBtnSvg}
                alt=""
                className={classes.videoRequestCardIcon}
              />
              <Typography className={classes.videoRequestCardText}>
                {label}
              </Typography>
            </Box>
            <Box style={{ textAlign: "left" }}>
              <button
                type="button"
                className={classes.videoRequestSeeDetails}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  setVideoRequestModalMessage(msg);
                  setVideoRequestAgreeTerms(false);
                  setVideoRequestModalPrice(
                    String(msg.videoRequest?.price ?? msg.price ?? "")
                  );
                  setVideoRequestModalRequest("");
                  setVideoRequestModalFrom("");
                }}
              >
                See Details
              </button>
            </Box>
          </Box>
        </Box>
      );
    } else if (msg.type === "notification") {
      const isRejection = msg.content?.startsWith("✕");
      const isAccept = msg.content?.includes(
        "You accepted custom video request"
      );
      return (
        <Box className={classes.notificationContainer}>
          <Typography
            className={classes.notificationText}
            style={isRejection || isAccept ? { color: "#FFFFFF" } : undefined}
          >
            {msg.content}
          </Typography>
        </Box>
      );
    } else if (msg.type === "tip") {
      const amountRaw = msg.tipAmount ?? msg.price;
      const amount =
        typeof amountRaw === "number"
          ? amountRaw
          : Number(String(amountRaw).replace(/[^\d.]/g, ""));
      const amountText = Number.isFinite(amount)
        ? `$${amount}`
        : `$${amountRaw}`;
      const label = isOutgoing ? "You`ve sent" : "You`ve received";

      return (
        <Box
          className={
            classes.tipContainer +
            " " +
            (isOutgoing
              ? classes.tipContainerOutgoing
              : classes.tipContainerIncoming)
          }
        >
          <img
            src={TipIconSvg}
            alt=""
            className={
              classes.tipIconImg +
              " " +
              (isOutgoing
                ? classes.tipIconImgOutgoing
                : classes.tipIconImgIncoming)
            }
          />
          <Typography className={classes.tipText}>
            {label} <span className={classes.tipBold}>{amountText}</span>{" "}
            <span className={classes.tipBold}>Tip</span>
          </Typography>
        </Box>
      );
    }
    const isRejectionMessage =
      msg.type === "text" &&
      (msg.content?.startsWith("✕") ||
        msg.content?.startsWith("✓") ||
        msg.content?.includes("You accepted custom video request") ||
        msg.content?.includes("You rejected custom video request"));

    const statusPrefix = (() => {
      const content = msg.content || "";
      if (content.startsWith("✕"))
        return {
          icon: "✕",
          kind: "rejected" as const,
          text: content.replace(/^✕\s*/, ""),
        };
      if (content.startsWith("✓"))
        return {
          icon: "✓",
          kind: "accepted" as const,
          text: content.replace(/^✓\s*/, ""),
        };
      if (content.includes("You accepted custom video request")) {
        return { icon: "✓", kind: "accepted" as const, text: content };
      }
      return null;
    })();

    return (
      <Box
        style={
          isRejectionMessage
            ? {
              backgroundColor: "#FF78AA",
              color: "#FFFFFF",
              padding: "10px 14px",
              borderRadius: "8px",
            }
            : undefined
        }
      >
        <Typography
          style={
            msg.content?.startsWith("✕") ||
              msg.content?.includes("You accepted custom video request")
              ? { color: "#FFFFFF" }
              : undefined
          }
        >
          {isRejectionMessage && statusPrefix ? (
            <Box display="flex" alignItems="center" style={{ gap: 6 }}>
              <span>{statusPrefix.icon}</span>
              <span>
                {isOutgoing
                  ? statusPrefix.text
                  : statusPrefix.kind === "accepted"
                    ? "Your custom video request was accepted"
                    : "Your custom video request was rejected"}
              </span>
            </Box>
          ) : (
            msg.content
          )}
        </Typography>
      </Box>
    );
  };

  const handleSearchTypeChange = (type: "users" | "messages") => {
    setSearchType(type);
    setSearchAnchorEl(null);
    if (type === "users") {
      setSearchQuery("");
    } else {
      setSearchQuery("");
    }
  };

  const getConversationParticipant = (convId: string, msg: Message) => {
    const conv = conversations.find(c => c._id === convId) || {
      participant: {
        _id: msg.senderId,
        firstName: msg.sender.username || "Unknown",
        lastName: "",
        profileImg: msg.sender.profileImg,
        status: userStatuses[msg.senderId] || "offline",
      },
    };
    return conv.participant;
  };

  const handleEmojiSelect = (emoji: any) => {
    const emojiText = emoji?.native || emoji?.unified || "";
    const currentValue = inputValue;

    // Always use saved cursor position (saved before picker interaction)
    const start = cursorPosition;
    const end = cursorPosition;

    // Insert emoji at saved cursor position
    const newValue =
      currentValue.substring(0, start) +
      emojiText +
      currentValue.substring(end);
    setInputValue(newValue);

    // Set cursor position after the inserted emoji and restore focus
    // Use multiple timeouts to ensure proper focus restoration
    setTimeout(() => {
      if (messageInputRef.current) {
        const input = messageInputRef.current;
        const newCursorPosition = start + emojiText.length;

        // First, focus the input
        input.focus();

        // Then set cursor position after a small delay to ensure DOM is updated
        setTimeout(() => {
          input.setSelectionRange(newCursorPosition, newCursorPosition);
          setCursorPosition(newCursorPosition);
        }, 0);
      }
    }, 50);

    setShowEmojiPicker(false);
  };

  return (
    <>
      <StandardPageHelmet seo={seo} />

      <Dialog
        open={!!videoRequestModalMessage}
        onClose={() => {
          setVideoRequestModalMessage(null);
          setVideoRequestAgreeTerms(false);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: "#131313",
            color: "#fff",
            borderRadius: 12,
            maxWidth: 551,
          },
        }}
      >
        <DialogTitle style={{ padding: "16px 24px" }}>
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <Typography variant="h6" style={{ fontWeight: 600, flex: 1 }}>
              New video request
            </Typography>
            <IconButton
              size="small"
              onClick={() => {
                setVideoRequestModalMessage(null);
                setVideoRequestAgreeTerms(false);
              }}
              style={{ color: "#fff", marginLeft: 8 }}
              aria-label="Close"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent style={{ paddingTop: 8 }}>
          {videoRequestModalMessage && (
            <>
              <Box
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                  marginBottom: 16,
                }}
              >
                <Typography className={classes.videoRequestModalLabel}>
                  Price:
                </Typography>
                <TextField
                  fullWidth
                  value={videoRequestModalPrice}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setVideoRequestModalPrice(e.target.value)
                  }
                  variant="outlined"
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography
                          component="span"
                          style={{ color: "#FFD0E1" }}
                        >
                          $
                        </Typography>
                      </InputAdornment>
                    ),
                    style: { color: "#FFD0E1" },
                    className: classes.videoRequestModalInput,
                  }}
                />
              </Box>
              <Box
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                  marginBottom: 16,
                }}
              >
                <Typography className={classes.videoRequestModalLabel}>
                  From:{" "}
                  <Box component="span" style={{ color: "#FE5A97" }}>
                    *
                  </Box>
                </Typography>
                <TextField
                  fullWidth
                  required
                  placeholder="@sugarfan"
                  value={videoRequestModalFrom}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setVideoRequestModalFrom(e.target.value)
                  }
                  variant="outlined"
                  size="small"
                  InputProps={{
                    style: { color: "#fff" },
                    className: classes.videoRequestModalInput,
                  }}
                />
              </Box>
              <Box
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                  marginBottom: 16,
                }}
              >
                <Typography className={classes.videoRequestModalLabel}>
                  Request:{" "}
                  <Box component="span" style={{ color: "#FE5A97" }}>
                    *
                  </Box>
                </Typography>
                <TextField
                  fullWidth
                  required
                  value={videoRequestModalRequest}
                  placeholder="I want to get a video with request1 and request2"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setVideoRequestModalRequest(e.target.value)
                  }
                  variant="outlined"
                  size="small"
                  multiline
                  minRows={2}
                  InputProps={{
                    style: { color: "#fff" },
                    className: classes.videoRequestModalInput,
                  }}
                />
              </Box>
              <DialogContentText
                className={classes.videoRequestModalDisclaimer}
              >
                You'll receive your earnings after sending the video. If it
                isn't delivered within 7 business days, the request will be
                canceled automatically. You can reject the request at any time
                if you're unable to complete it.
              </DialogContentText>
              {videoRequestModalMessage.videoRequest?.status === "pending" &&
                videoRequestModalMessage.senderId !== userId && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={videoRequestAgreeTerms}
                        onChange={e =>
                          setVideoRequestAgreeTerms(e.target.checked)
                        }
                        className={classes.videoRequestModalCheckbox}
                        style={{ color: "#FFFFFF" }}
                      />
                    }
                    label={
                      <>
                        I agree to deliver this video within{" "}
                        <Box component="strong" fontWeight="bold">
                          7 business days
                        </Box>
                      </>
                    }
                    style={{ color: "#FFFFFF", marginBottom: 8 }}
                  />
                )}
            </>
          )}
        </DialogContent>
        {videoRequestModalMessage &&
          videoRequestModalMessage.videoRequest?.status === "pending" &&
          videoRequestModalMessage.senderId !== userId && (
            <DialogActions className={classes.videoRequestModalActions}>
              <Button
                disabled={
                  !videoRequestAgreeTerms ||
                  !videoRequestModalFrom.trim() ||
                  !videoRequestModalRequest.trim()
                }
                onClick={async () => {
                  if (videoRequestModalMessage) {
                    try {
                      await handleAcceptVideoRequest(videoRequestModalMessage);
                      setAcceptedRequestModalMessage(videoRequestModalMessage);
                      setAcceptedRequestFrom(videoRequestModalFrom);
                      setAcceptedRequestRequest(videoRequestModalRequest);
                    } catch {
                      // handleAcceptVideoRequest already shows toast
                    }
                  }
                  setVideoRequestModalMessage(null);
                  setVideoRequestAgreeTerms(false);
                }}
                className={classes.videoRequestModalAcceptBtn}
              >
                Accept request
              </Button>
              <Button
                onClick={() => {
                  if (videoRequestModalMessage) {
                    const reqId = videoRequestModalMessage.videoRequest?._id;
                    setRejectedVideoRequestId(reqId || null);
                    setRejectedMessage(videoRequestModalMessage);
                    handleRejectVideoRequest(videoRequestModalMessage);
                  }
                  setVideoRequestModalMessage(null);
                  setVideoRequestAgreeTerms(false);
                  setShowRejectedPopup(true);
                }}
                className={classes.videoRequestModalRejectBtn}
              >
                Reject
              </Button>
            </DialogActions>
          )}
      </Dialog>

      {/* Accepted request popup - after Accept */}
      <Dialog
        open={!!acceptedRequestModalMessage}
        onClose={() => {
          setAcceptedRequestModalMessage(null);
          setAcceptedRequestFrom("");
          setAcceptedRequestRequest("");
          setAcceptedRequestSelectedFile(null);
          setAcceptedRequestMessageToViewer("");
          setAcceptedRequestPreviewUrl(null);
          if (acceptedRequestVideoInputRef.current) {
            acceptedRequestVideoInputRef.current.value = "";
          }
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: "#131313",
            color: "#fff",
            borderRadius: 12,
            maxWidth: 551,
          },
        }}
      >
        {acceptedRequestModalMessage && (
          <>
            <DialogTitle style={{ padding: "16px 24px" }}>
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                justifyContent="space-between"
                width="100%"
              >
                <Typography variant="h6" style={{ fontWeight: 600, flex: 1 }}>
                  Accepted request
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => {
                    setAcceptedRequestModalMessage(null);
                    setAcceptedRequestFrom("");
                    setAcceptedRequestRequest("");
                    setAcceptedRequestSelectedFile(null);
                    setAcceptedRequestMessageToViewer("");
                    setAcceptedRequestPreviewUrl(null);
                    if (acceptedRequestVideoInputRef.current) {
                      acceptedRequestVideoInputRef.current.value = "";
                    }
                  }}
                  style={{ color: "#fff", marginLeft: 8 }}
                  aria-label="Close"
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent style={{ paddingTop: 8 }}>
              {acceptedRequestSelectedFile ? (
                <>
                  <Box
                    position="relative"
                    width="100%"
                    height={isMobile ? 398 : 500}
                    style={{
                      backgroundColor: "#1F1F1F",
                      borderRadius: 8,
                      overflow: "hidden",
                    }}
                  >
                    {acceptedRequestPreviewUrl && (
                      <video
                        src={acceptedRequestPreviewUrl}
                        controls
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                      />
                    )}
                    <IconButton
                      size="small"
                      onClick={() => {
                        setAcceptedRequestSelectedFile(null);
                        setAcceptedRequestMessageToViewer("");
                        if (acceptedRequestVideoInputRef.current) {
                          acceptedRequestVideoInputRef.current.value = "";
                        }
                      }}
                      style={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        color: "#fff",
                        backgroundColor: "rgba(0,0,0,0.5)",
                        padding: 6,
                      }}
                      aria-label="Change video"
                    >
                      <CloseIcon />
                    </IconButton>
                  </Box>
                  <Box style={{ marginTop: 16 }}>
                    <Typography
                      className={classes.videoRequestModalLabel}
                      style={{ marginBottom: 8 }}
                    >
                      You may leave a message to a video viewer
                    </Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      multiline
                      minRows={3}
                      placeholder="Type your message..."
                      value={acceptedRequestMessageToViewer}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setAcceptedRequestMessageToViewer(e.target.value)
                      }
                      InputProps={{
                        style: { color: "#fff" },
                        className: classes.videoRequestModalInput,
                      }}
                    />
                  </Box>
                </>
              ) : (
                <>
                  <Box
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                      marginBottom: 16,
                    }}
                  >
                    <Typography className={classes.videoRequestModalLabel}>
                      Delivery deadline:
                    </Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={format(
                        addDays(
                          new Date(
                            acceptedRequestModalMessage.videoRequest
                              ?.createdAt ||
                            acceptedRequestModalMessage.createdAt
                          ),
                          7
                        ),
                        "yyyy-MM-dd"
                      )}
                      InputProps={{
                        readOnly: true,
                        style: { color: "#fff" },
                        className: classes.videoRequestModalInput,
                      }}
                    />
                  </Box>
                  <Box
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                      marginBottom: 16,
                    }}
                  >
                    <Typography className={classes.videoRequestModalLabel}>
                      From:
                    </Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={acceptedRequestFrom}
                      InputProps={{
                        readOnly: true,
                        style: { color: "#fff" },
                        className: classes.videoRequestModalInput,
                      }}
                    />
                  </Box>
                  <Box
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                      marginBottom: 16,
                    }}
                  >
                    <Typography className={classes.videoRequestModalLabel}>
                      Request:
                    </Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      multiline
                      minRows={2}
                      value={acceptedRequestRequest}
                      InputProps={{
                        readOnly: true,
                        style: { color: "#fff" },
                        className: classes.videoRequestModalInput,
                      }}
                    />
                  </Box>
                  <input
                    type="file"
                    accept="video/*"
                    ref={acceptedRequestVideoInputRef}
                    style={{ display: "none" }}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const file = e.target.files?.[0];
                      if (!file || !acceptedRequestModalMessage) return;
                      if (!file.type.startsWith("video/")) {
                        showToast("Please select a video file.", "error");
                        e.target.value = "";
                        return;
                      }
                      setAcceptedRequestSelectedFile(file);
                    }}
                  />
                </>
              )}
            </DialogContent>
            <DialogActions className={classes.videoRequestModalActions}>
              {acceptedRequestSelectedFile ? (
                <>
                  <Button
                    onClick={() => {
                      setAcceptedRequestSelectedFile(null);
                      setAcceptedRequestMessageToViewer("");
                      if (acceptedRequestVideoInputRef.current) {
                        acceptedRequestVideoInputRef.current.value = "";
                      }
                    }}
                    className={classes.videoRequestModalRejectBtn}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={async () => {
                      if (
                        !acceptedRequestModalMessage ||
                        !acceptedRequestSelectedFile
                      )
                        return;
                      try {
                        await handleUploadVideoForRequest(
                          acceptedRequestModalMessage,
                          acceptedRequestSelectedFile
                        );
                        if (
                          selectedConversationId &&
                          acceptedRequestMessageToViewer.trim()
                        ) {
                          await sendMessageAPI(
                            {
                              conversationId: selectedConversationId,
                              recipientId: acceptedRequestModalMessage.senderId,
                              content: acceptedRequestMessageToViewer.trim(),
                              type: "text",
                              isPaid: false,
                              price: 0,
                            },
                            []
                          );
                        }
                        setAcceptedRequestModalMessage(null);
                        setAcceptedRequestFrom("");
                        setAcceptedRequestRequest("");
                        setAcceptedRequestSelectedFile(null);
                        setAcceptedRequestMessageToViewer("");
                        if (acceptedRequestVideoInputRef.current) {
                          acceptedRequestVideoInputRef.current.value = "";
                        }
                      } catch {
                        // toast already shown in handler
                      }
                    }}
                    className={classes.videoRequestModalAcceptBtn}
                  >
                    Send
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    onClick={() =>
                      acceptedRequestVideoInputRef.current?.click()
                    }
                    className={classes.videoRequestModalAcceptBtn}
                    startIcon={<Upload size={18} style={{ flexShrink: 0 }} />}
                  >
                    Upload video
                  </Button>
                  <Button
                    onClick={async () => {
                      if (!acceptedRequestModalMessage?.videoRequest?._id)
                        return;
                      try {
                        await setVideoRequestPendingAPI(
                          acceptedRequestModalMessage.videoRequest._id
                        );
                        setMessages(prev =>
                          prev.map(msg =>
                            msg._id === acceptedRequestModalMessage._id
                              ? {
                                ...msg,
                                videoRequest: {
                                  ...msg.videoRequest!,
                                  status: "pending",
                                },
                              }
                              : msg
                          )
                        );
                        showToast("Request set back to pending.", "success");
                        setAcceptedRequestModalMessage(null);
                        setAcceptedRequestFrom("");
                        setAcceptedRequestRequest("");
                      } catch (err: any) {
                        showToast(
                          err?.message || "Failed to set request to pending",
                          "error"
                        );
                      }
                    }}
                    className={classes.videoRequestModalRejectBtn}
                  >
                    Reject
                  </Button>
                </>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      <RejectVideoRequestSuccessModal
        open={showRejectedPopup}
        onClose={() => {
          setShowRejectedPopup(false);
          setRejectedVideoRequestId(null);
          setRejectedMessage(null);
        }}
        buttonText="Continue to Sugar cribs"
        onCancelRejection={async () => {
          if (!rejectedVideoRequestId) return;
          try {
            await setVideoRequestPendingAPI(rejectedVideoRequestId);
            if (rejectedMessage) {
              setMessages(prev =>
                prev.map(msg =>
                  msg._id === rejectedMessage._id
                    ? {
                      ...msg,
                      videoRequest: {
                        ...msg.videoRequest!,
                        status: "pending",
                      },
                    }
                    : msg
                )
              );
            }
            showToast("Rejection cancelled", "success");
          } catch (err: any) {
            showToast(err?.message || "Failed to cancel rejection", "error");
          } finally {
            setShowRejectedPopup(false);
            setRejectedVideoRequestId(null);
            setRejectedMessage(null);
          }
        }}
      />

      <Box className={classes.messagesRoot}>
        <Box className={classes.messagesContainer}>
          <Grid className={classes.messagesMain}>
            <Grid
              item
              md={4}
              className={
                classes.chatSidebar +
                (!showSidebar ? " " + classes.chatSidebarHidden : "")
              }
            >
              <Box className={classes.header}>
                <Typography className={classes.headerTitle}>
                  My messages
                </Typography>
                <IconButton
                  className={classes.newChatButton}
                  onClick={() => setOpenModal(true)}
                  aria-label="Create a new chat"
                >
                  <AddIcon />
                </IconButton>
                <GroupChatModal
                  open={openModal}
                  onClose={() => setOpenModal(false)}
                  onCreate={handleCreateGroup}
                />
              </Box>
              <Box className={classes.searchWrapper}>
                <InputBase
                  className={classes.searchInput}
                  placeholder="Search..."
                  fullWidth
                  value={searchQuery}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setSearchQuery(e.target.value)
                  }
                  startAdornment={
                    <InputAdornment position="start">
                      <SearchIcon style={{ color: "#FFFFFF", fontSize: 20 }} />
                    </InputAdornment>
                  }
                />
              </Box>
              <Box className={classes.chatList}>
                {loading &&
                  conversations.length === 0 &&
                  globalMessages.length === 0 ? (
                  <Box display="flex" justifyContent="center" padding={2}>
                    <CircularProgress size={24} />
                  </Box>
                ) : searchType === "messages" && searchQuery ? (
                  globalMessages.length > 0 ? (
                    globalMessages.map(msg => {
                      const participant = getConversationParticipant(
                        msg.conversationId,
                        msg
                      );
                      return (
                        <div
                          key={msg._id}
                          className={
                            msg.conversationId === selectedConversationId
                              ? classes.chatItemActive
                              : classes.chatItem
                          }
                          onClick={() => handleChatSelect(msg.conversationId)}
                        >
                          <div className={classes.chatItemTop}>
                            <div className={classes.chatItemLeft}>
                              <Avatar
                                src={
                                  loadingMedia[participant?.profileImg || ""]
                                    ? undefined
                                    : mediaUrls[
                                    participant?.profileImg || ""
                                    ] || defaultAvatars
                                }
                              >
                                {loadingMedia[
                                  participant?.profileImg || ""
                                ] && <CircularProgress size={24} />}
                              </Avatar>
                              <div className={classes.chatItemName}>
                                <Typography className={classes.chatNameItem}>
                                  {participant?.firstName}{" "}
                                  {participant?.lastName}
                                </Typography>
                                <Typography className={classes.chatNameWrites}>
                                  {participant &&
                                    participant._id &&
                                    typingUsers.includes(participant._id)
                                    ? "...writes"
                                    : participant && participant._id
                                      ? buildParticipantStatus(
                                        participant,
                                        Boolean(userStatuses[participant._id])
                                      )
                                      : ""}{" "}
                                </Typography>
                              </div>
                            </div>
                            <Typography className={classes.chatTime}>
                              {msg.createdAt
                                ? formatConversationTime(msg.createdAt)
                                : "N/A"}
                            </Typography>
                          </div>
                          <Box className={classes.chatText}>
                            <Typography className={classes.chatMessage}>
                              {msg.content || "No content"}
                            </Typography>
                          </Box>
                        </div>
                      );
                    })
                  ) : (
                    <Typography
                      style={{ padding: "20px", textAlign: "center" }}
                    >
                      No Chat
                    </Typography>
                  )
                ) : conversations.length === 0 ? (
                  <Typography>No chats</Typography>
                ) : (
                  conversations.map(conv => (
                    <div
                      key={conv._id}
                      className={
                        conv._id === selectedConversationId
                          ? classes.chatItemActive
                          : classes.chatItem
                      }
                      onClick={() => handleChatSelect(conv._id)}
                      onTouchStart={e => handleTouchStart(e, conv._id)}
                      onTouchEnd={e => handleTouchEnd(e, conv._id)}
                    >
                      <div className={classes.chatItemTop}>
                        <div className={classes.chatItemLeft}>
                          <Avatar
                            className={classes.chatItemAvatar}
                            src={
                              loadingMedia[
                                conv.isGroup
                                  ? conv.groupImage || ""
                                  : conv.participant?.profileImg || ""
                              ]
                                ? undefined
                                : mediaUrls[
                                conv.isGroup
                                  ? conv.groupImage || ""
                                  : conv.participant?.profileImg || ""
                                ] || defaultAvatars
                            }
                          >
                            {loadingMedia[
                              conv.isGroup
                                ? conv.groupImage || ""
                                : conv.participant?.profileImg || ""
                            ] && <CircularProgress size={24} />}
                          </Avatar>
                          <div className={classes.chatItemName}>
                            <Typography className={classes.chatNameItem}>
                              {conv.isGroup
                                ? conv.groupName ||
                                conv.participants
                                  .map(p => `${p.firstName} ${p.lastName}`)
                                  .join(", ")
                                : `${conv.participant?.firstName} ${conv.participant?.lastName}`}
                            </Typography>
                            <Typography className={classes.chatNameWrites}>
                              {conv.isGroup
                                ? conv.participants.some(p =>
                                  typingUsers.includes(p._id)
                                )
                                  ? "...writes"
                                  : conv.lastMessage?.content || "No messages"
                                : conv.participant?._id &&
                                  typingUsers.includes(conv.participant._id)
                                  ? "...writes"
                                  : conv.lastMessage?.content || "No messages"}
                            </Typography>
                          </div>
                        </div>
                        <Typography className={classes.chatTime}>
                          {conv.isGroup
                            ? conv.lastMessage?.createdAt
                              ? formatTimeLastOnline(conv.lastMessage.createdAt)
                              : "N/A"
                            : formatTimeLastOnline(
                              canShowOnlineStatus(conv.participant)
                                ? conv.participant?.lastActive ||
                                conv.lastMessage?.createdAt
                                : conv.lastMessage?.createdAt
                            )}
                        </Typography>
                      </div>
                    </div>
                  ))
                )}
              </Box>
            </Grid>
            <Grid
              className={
                classes.chatWindow +
                (showSidebar && isMobile ? " " + classes.chatWindowHidden : "")
              }
            >
              <Box className={classes.chatHeader}>
                {isMobile && selectedConversationId && !showSidebar && (
                  <IconButton
                    className={classes.mobileBackButton}
                    onClick={handleBackToChats}
                  >
                    <img src={ArrowBackSvg} alt="Back" />
                  </IconButton>
                )}
                {currentConversation && (
                  <Box className={classes.chatUserInfo}>
                    {currentConversation.isGroup ? (
                      <>
                        <Avatar
                          className={classes.chatHeaderAvatar}
                          src={
                            loadingMedia[currentConversation.groupImage || ""]
                              ? undefined
                              : mediaUrls[
                              currentConversation.groupImage || ""
                              ] || defaultAvatars
                          }
                        >
                          {loadingMedia[
                            currentConversation.groupImage || ""
                          ] && <CircularProgress size={24} />}
                        </Avatar>
                        <Box>
                          <Typography className={classes.chatUser}>
                            {currentConversation.groupName ||
                              currentConversation.participants
                                .map(p => `${p.firstName} ${p.lastName}`)
                                .join(", ")}
                          </Typography>
                        </Box>
                      </>
                    ) : (
                      <>
                        <Link
                          to={`/${currentConversation.participant?.username}`}
                          className={classes.nameLink}
                        >
                          <Avatar
                            className={classes.chatHeaderAvatar}
                            src={
                              loadingMedia[
                                currentConversation.participant?.profileImg ||
                                ""
                              ]
                                ? undefined
                                : mediaUrls[
                                currentConversation.participant
                                  ?.profileImg || ""
                                ] || defaultAvatars
                            }
                          >
                            {loadingMedia[
                              currentConversation.participant?.profileImg || ""
                            ] && <CircularProgress size={24} />}
                          </Avatar>
                        </Link>
                        <Box>
                          <Link
                            to={`/${currentConversation.participant?.username}`}
                            className={classes.nameLink}
                          >
                            <Typography className={classes.chatUser}>
                              {currentConversation.participant?.firstName}{" "}
                              {currentConversation.participant?.lastName}{" "}
                              <VerifiedShieldHelpIcon
                                style={{ marginLeft: "3px" }}
                                className={classes.verifiedIcon}
                              />
                            </Typography>
                          </Link>
                          <Typography className={classes.chatUserNameHelp}>
                            @{currentConversation.participant?.username}
                          </Typography>
                        </Box>
                      </>
                    )}
                  </Box>
                )}
                {!currentConversation ? null : (
                  <Box margin={"0 0 0 auto"}>
                    <EllipsisMenuButtonProfile
                      profileId={
                        currentConversation.participant
                          ? currentConversation.participant._id
                          : currentConversation._id
                      }
                      typeReport="profile"
                      refreshConversations={refreshAfterBlockUser}
                    />
                  </Box>
                )}
                {/* {currentConversation && (
                  <Box className={classes.chatHeaderActions}>
                    <IconButton
                      className={classes.chatHeaderActionButton}
                      onClick={(e: React.MouseEvent<HTMLElement>) =>
                        setHeaderMenuAnchor(e.currentTarget)
                      }
                    >
                      <MoreVerticalIcon />
                    </IconButton>
                    <Menu
                      anchorEl={headerMenuAnchor}
                      open={Boolean(headerMenuAnchor)}
                      onClose={() => setHeaderMenuAnchor(null)}
                      className={classes.menu}
                    >
                      {!currentConversation.isGroup
                        ? [
                          isUserMuted(
                            currentConversation.participant?._id || ""
                          ) ? (
                            <MenuItem
                              key="unmute"
                              onClick={() => {
                                unmuteUser(
                                  currentConversation.participant?._id || ""
                                );
                                setHeaderMenuAnchor(null);
                              }}
                              className={classes.menuItemUnmute}
                            >
                              Unmute User
                            </MenuItem>
                          ) : (
                            <MenuItem
                              key="mute"
                              onClick={(e: React.MouseEvent<HTMLElement>) => {
                                setMuteMenuAnchor(e.currentTarget);
                              }}
                              className={classes.menuItemMute}
                            >
                              Mute User
                            </MenuItem>
                          ),
                          <MenuItem
                            key="block"
                            onClick={() => {
                              setHeaderMenuAnchor(null);
                              setBlockUserId(
                                currentConversation.participant?._id || null
                              );
                              setBlockModalOpen(true);
                            }}
                            className={classes.menuItemBlock}
                          >
                            Block User
                          </MenuItem>,
                          <MenuItem
                            key="report"
                            onClick={() => {
                              setHeaderMenuAnchor(null);
                              // TODO: Implement report functionality
                              showToast(
                                "Report functionality coming soon",
                                "success"
                              );
                            }}
                            className={classes.menuItemReport}
                          >
                            Report User
                          </MenuItem>,
                          <MenuItem
                            key="delete-conversation"
                            onClick={() => {
                              setHeaderMenuAnchor(null);
                              setDeleteConversationModalOpen(true);
                            }}
                            className={classes.menuItemBlock}
                            style={{ color: "#f44336" }}
                          >
                            Delete Conversation
                          </MenuItem>,
                        ]
                        : [
                          // Group chat options
                          <MenuItem
                            key="report-group"
                            onClick={() => {
                              setHeaderMenuAnchor(null);
                              // TODO: Implement report group functionality
                              showToast(
                                "Report group functionality coming soon",
                                "success"
                              );
                            }}
                            className={classes.menuItemReport}
                          >
                            Report Group
                          </MenuItem>,
                          <MenuItem
                            key="leave-group"
                            onClick={() => {
                              setHeaderMenuAnchor(null);
                              // TODO: Implement leave group functionality
                              showToast(
                                "Leave group functionality coming soon",
                                "success"
                              );
                            }}
                            className={classes.menuItemBlock}
                          >
                            Leave Group
                          </MenuItem>,
                          <MenuItem
                            key="delete-group-conversation"
                            onClick={() => {
                              setHeaderMenuAnchor(null);
                              setDeleteConversationModalOpen(true);
                            }}
                            className={classes.menuItemBlock}
                            style={{ color: "#f44336" }}
                          >
                            Delete Conversation
                          </MenuItem>,
                        ]}
                    </Menu>
                    <Menu
                      anchorEl={muteMenuAnchor}
                      open={Boolean(muteMenuAnchor)}
                      onClose={() => setMuteMenuAnchor(null)}
                      className={classes.menu}
                    >
                      <MenuItem
                        onClick={() => handleMuteUser("1 hour")}
                        className={classes.menuItemMuteOption}
                      >
                        1 Hour
                      </MenuItem>
                      <MenuItem
                        onClick={() => handleMuteUser("1 day")}
                        className={classes.menuItemMuteOption}
                      >
                        1 Day
                      </MenuItem>
                      <MenuItem
                        onClick={() => handleMuteUser("1 week")}
                        className={classes.menuItemMuteOption}
                      >
                        1 Week
                      </MenuItem>
                      <MenuItem
                        onClick={() => handleMuteUser("1 month")}
                        className={classes.menuItemMuteOption}
                      >
                        1 Month
                      </MenuItem>
                    </Menu>
                  </Box>
                )} */}
              </Box>
              <div className={classes.messagesArea} ref={messagesAreaRef}>
                {loading && messages.length === 0 ? (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    padding={4}
                    minHeight="200px"
                  >
                    <CircularProgress size={32} style={{ color: "#FE5A97" }} />
                  </Box>
                ) : messages.length === 0 && !loading ? (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    padding={4}
                    minHeight="200px"
                  >
                    <Typography style={{ color: "#A3A3A3" }}>
                      No messages
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {messages.map((msg, idx) => {
                      const previousMessage =
                        idx > 0 ? messages[idx - 1] : undefined;
                      const showDate = shouldShowDate(
                        msg,
                        previousMessage,
                        idx
                      );

                      return (
                        <React.Fragment key={msg._id}>
                          {showDate && (
                            <Box className={classes.dateSeparator}>
                              <Typography variant="caption">
                                {formatDateSeparator(msg.createdAt)}
                              </Typography>
                            </Box>
                          )}
                          <div
                            className={
                              classes.messageContainer +
                              " " +
                              (msg.senderId === userId
                                ? classes.messageContainerRight
                                : classes.messageContainerLeft)
                            }
                          >
                            {msg.senderId !== userId ? (
                              <>
                                <Link
                                  to={`/${msg.sender._id}`}
                                  className={classes.nameLink}
                                >
                                  <Avatar
                                    className={classes.messageAvatar}
                                    src={
                                      loadingMedia[msg.sender.profileImg || ""]
                                        ? undefined
                                        : mediaUrls[
                                        msg.sender.profileImg || ""
                                        ] || defaultAvatars
                                    }
                                    style={{ marginRight: "10px" }}
                                  >
                                    {loadingMedia[
                                      msg.sender.profileImg || ""
                                    ] && <CircularProgress size={24} />}
                                  </Avatar>
                                </Link>
                                <Box
                                  className={
                                    classes.messageWrapper +
                                    " " +
                                    classes.messageWrapperLeft
                                  }
                                >
                                  <div className={classes.messageContent}>
                                    <Box
                                      className={
                                        classes.messageLeft +
                                        (msg.type === "tip"
                                          ? " " + classes.messageLeftTip
                                          : "") +
                                        (msg.type === "video_request"
                                          ? " " +
                                          classes.messageLeftVideoRequest
                                          : "") +
                                        (msg.type === "text" &&
                                          (msg.content?.startsWith("✕") ||
                                            msg.content?.includes(
                                              "You accepted custom video request"
                                            ))
                                          ? " " + classes.messageLeftRejection
                                          : "")
                                      }
                                    >
                                      {renderMessageContent(msg, false)}
                                    </Box>
                                  </div>
                                  <div
                                    className={
                                      classes.messageInfo +
                                      " " +
                                      classes.messageInfoLeft
                                    }
                                  >
                                    <Typography className={classes.messageTime}>
                                      {formatTime(msg.createdAt)}
                                    </Typography>
                                  </div>
                                </Box>
                              </>
                            ) : (
                              <>
                                <Box
                                  className={
                                    classes.messageWrapper +
                                    " " +
                                    classes.messageWrapperRight
                                  }
                                >
                                  <div className={classes.messageContent}>
                                    <Box
                                      className={
                                        classes.messageRight +
                                        (msg.type === "tip"
                                          ? " " + classes.messageRightTip
                                          : "") +
                                        (msg.type === "image" ||
                                          msg.type === "video"
                                          ? " " + classes.messageRightMedia
                                          : "") +
                                        (msg.type === "text" &&
                                          (msg.content?.startsWith("✕") ||
                                            msg.content?.includes(
                                              "You accepted custom video request"
                                            ))
                                          ? " " + classes.messageRightRejection
                                          : "")
                                      }
                                    >
                                      {renderMessageContent(msg, true)}
                                    </Box>
                                  </div>
                                  <div
                                    className={
                                      classes.messageInfo +
                                      " " +
                                      classes.messageInfoRight
                                    }
                                  >
                                    <Typography className={classes.messageTime}>
                                      {formatTime(msg.createdAt)}
                                    </Typography>
                                    <div className={classes.messageStatus}>
                                      {msg.status === "read" ? (
                                        <DoneAllIcon
                                          className={classes.messageStatusRead}
                                        />
                                      ) : (
                                        <CheckIcon
                                          className={classes.messageStatusSent}
                                        />
                                      )}
                                    </div>
                                  </div>
                                </Box>
                                <Avatar
                                  className={classes.messageAvatar}
                                  src={
                                    loadingMedia[user?.profileImg || ""]
                                      ? undefined
                                      : mediaUrls[user?.profileImg || ""] ||
                                      defaultAvatars
                                  }
                                  style={{ marginLeft: "10px" }}
                                >
                                  {loadingMedia[user?.profileImg || ""] && (
                                    <CircularProgress size={24} />
                                  )}
                                </Avatar>
                              </>
                            )}
                          </div>
                        </React.Fragment>
                      );
                    })}
                    {/* Loader for older messages at the bottom */}
                    {hasMoreMessages && (
                      <div ref={loadMoreMessagesRef}>
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          padding={1.5}
                          minHeight={loadingMore ? 50 : 20}
                        >
                          {loadingMore && (
                            <Box
                              display="flex"
                              alignItems="center"
                              style={{ color: "#FE5A97" }}
                            >
                              <CircularProgress
                                size={20}
                                style={{ color: "#FE5A97" }}
                              />
                              <Typography
                                variant="caption"
                                style={{
                                  color: "#A3A3A3",
                                  fontSize: "12px",
                                  marginLeft: "8px",
                                }}
                              >
                                Loading older messages...
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </div>
                    )}
                  </>
                )}
              </div>
              <Menu
                anchorEl={messageMenuAnchor}
                open={messageMenuOpen !== null}
                onClose={handleMessageMenuClose}
                className={classes.messageMenu}
              >
                <MenuItem
                  onClick={() =>
                    handleMessageAction("delete", messageMenuOpen!)
                  }
                >
                  delete
                </MenuItem>
              </Menu>
              {currentConversation && (
                <Box className={classes.chatInputRow}>
                  <Box className={classes.pinkBorderButton}
                    onClick={() => {
                      setRequestVideoDialog(true);
                      setShowAttachmentMenu(false);
                    }}>
                    <img
                      src={sendVideoRequestIcon}
                      alt="Send video request"
                      style={isMobile ? { width: 16, height: 16 } : { width: 28, height: 28 }}
                    />
                  </Box>
                  <Box className={classes.pinkBorderButton}
                    onClick={() => {
                      setTipDialogVisible(true);
                      setShowAttachmentMenu(false);
                    }}>
                    <DollarSign size={isMobile ? 16 : 28} color="#fff" strokeWidth={2.5} />
                  </Box>
                  <Box className={classes.chatInputWrapper}>
                    <input
                      type="file"
                      accept={allowedImageExtensions
                        .map(ext => `.${ext}`)
                        .join(",")}
                      className={classes.fileInput}
                      id="photo-upload"
                      ref={photoInputRef}
                      onChange={e => handleFileUpload(e, "image")}
                    />
                    <input
                      type="file"
                      accept={allowedVideoExtensions
                        .map(ext => `.${ext}`)
                        .join(",")}
                      className={classes.fileInput}
                      id="video-upload"
                      ref={videoInputRef}
                      onChange={e => handleFileUpload(e, "video")}
                    />
                    {mediaPreviewUrl &&
                      (mediaType === "video" ? (
                        <video
                          src={mediaPreviewUrl}
                          className={classes.mediaPreview}
                          style={{ maxHeight: 120 }}
                          controls
                          muted
                        />
                      ) : (
                        <img
                          src={mediaPreviewUrl}
                          alt="Selected media preview"
                          className={classes.mediaPreview}
                        />
                      ))}
                    <InputBase
                      ref={messageInputRef}
                      placeholder="Your message"
                      className={classes.chatInput}
                      fullWidth
                      value={inputValue}
                      onChange={handleInputChange}
                      onMouseDown={(e: React.MouseEvent<HTMLInputElement>) => {
                        setTimeout(() => {
                          if (messageInputRef.current) {
                            setCursorPosition(
                              messageInputRef.current.selectionStart || 0
                            );
                          }
                        }, 0);
                      }}
                      onClick={(e: React.MouseEvent<HTMLInputElement>) => {
                        const target = e.target as HTMLInputElement;
                        setCursorPosition(target.selectionStart || 0);
                      }}
                      onMouseUp={(e: React.MouseEvent<HTMLInputElement>) => {
                        const target = e.target as HTMLInputElement;
                        setCursorPosition(target.selectionStart || 0);
                      }}
                      onSelect={(e: React.SyntheticEvent<HTMLInputElement>) => {
                        const target = e.target as HTMLInputElement;
                        setCursorPosition(target.selectionStart || 0);
                      }}
                      onKeyUp={(e: React.KeyboardEvent<HTMLInputElement>) => {
                        const target = e.target as HTMLInputElement;
                        setCursorPosition(target.selectionStart || 0);
                      }}
                      onKeyDown={e => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                      onFocus={() => {
                        if (isMobile && messageInputRef.current) {
                          const isIOS =
                            /iPad|iPhone|iPod/.test(navigator.userAgent) ||
                            (navigator.platform === "MacIntel" &&
                              navigator.maxTouchPoints > 1);
                          const delay = isIOS ? 600 : 400;
                          setTimeout(() => {
                            focusAndCenterInput(messageInputRef);
                          }, delay);
                        }
                      }}
                    />
                    {!isMobile && <Box display="flex" alignItems="center" fontSize={14}>
                      <CoinIcon stroke={"#A3A3A3"} />
                      <span style={{ marginLeft: 4, color: "#A3A3A3" }}>1</span>
                    </Box>}
                    <div className={classes.attachmentContainer}>
                      <IconButton
                        ref={emojiButtonRef}
                        onClick={e => {
                          e.stopPropagation();
                          setShowEmojiPicker(prev => !prev);
                        }}
                        className={classes.attachmentButton}
                        disabled={isUploadingMedia}
                        style={{
                          opacity: isUploadingMedia ? 0.5 : 1,
                        }}
                      >
                        <SentimentSatisfiedAlt
                          style={{ color: "#FFD0E1", fontSize: 22 }}
                        />
                      </IconButton>
                      <IconButton
                        onClick={() => setUploadMediaModalOpen(true)}
                        className={classes.attachmentButton}
                        disabled={isUploadingMedia}
                        style={{
                          opacity: isUploadingMedia ? 0.5 : 1,
                        }}
                      >
                        <img
                          src={PlusIconSvg}
                          alt=""
                          className={classes.plusIconImg}
                        />
                      </IconButton>
                    </div>
                    {/* {showAttachmentMenu && (
                      <Box className={classes.customAttachmentMenu}>
                        {!currentConversation.isGroup && (
                          <>
                            <IconButton
                              onClick={() => {
                                setRequestVideoDialog(true);
                                setShowAttachmentMenu(false);
                              }}
                              className={classes.attachmentButtonHelp}
                            >
                              <FilmIcon />
                            </IconButton>
                            <IconButton
                              onClick={() => {
                                setTipDialogVisible(true);
                                setShowAttachmentMenu(false);
                              }}
                              className={classes.attachmentButtonHelp}
                            >
                              <FShapeIcon />
                            </IconButton>
                          </>
                        )}
                        <IconButton
                          onClick={() => {
                            setUploadMediaModalOpen(true);
                            setShowAttachmentMenu(false);
                          }}
                          className={classes.attachmentButtonHelp}
                        >
                          <ImageIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => {
                            setUploadMediaModalOpen(true);
                            setShowAttachmentMenu(false);
                          }}
                          className={classes.attachmentButtonHelp}
                        >
                          <PlayCircleOutlineIcon />
                        </IconButton>
                      </Box>
                    )} */}
                  </Box>
                  {showEmojiPicker && (
                    <div ref={emojiPickerRef} className={classes.boxSmile}>
                      <Picker
                        data={data}
                        onEmojiSelect={handleEmojiSelect}
                        theme="dark"
                      />
                    </div>
                  )}
                  <IconButton
                    className={classes.sendButton}
                    onClick={handleSendMessage}
                    disabled={!inputValue.trim() && selectedFiles.length === 0}
                  >
                    <img
                      src={SendIconSvg}
                      alt=""
                      className={classes.sendIconImg}
                    />
                  </IconButton>
                </Box>
              )}
              <Modal
                open={mediaDialogVisible}
                onClose={() => {
                  setMediaDialogVisible(false);
                  setSelectedFiles([]);
                  setMediaPreviewUrl(null);
                }}
              >
                <Box className={classes.mediaModalButon}>
                  <DialogTitle className={classes.mediaModalTitle}>
                    Send {mediaType === "video" ? "Video" : "Photo"}
                  </DialogTitle>
                  <DialogContent>
                    {mediaPreviewUrl &&
                      (mediaType === "image" ? (
                        <img
                          src={mediaPreviewUrl}
                          alt="Preview"
                          style={{
                            width: "100%",
                            maxHeight: 300,
                            objectFit: "contain",
                          }}
                        />
                      ) : (
                        <video
                          controls
                          style={{ width: "100%", maxHeight: 300 }}
                        >
                          <source src={mediaPreviewUrl} type="video/mp4" />
                        </video>
                      ))}
                    <Box
                      mt={2}
                      width="90%"
                      display="flex"
                      justifyContent="space-between"
                      className={classes.mediaModalContentInfo}
                    >
                      <select
                        value={isPaidMedia ? "Fixed Price" : "Free"}
                        onChange={e => {
                          const value = e.target.value;
                          setIsPaidMedia(value === "Fixed Price");
                        }}
                        style={{
                          width: "48%",
                          backgroundColor: "#1e1e1e",
                          color: "#fff",
                          border: "1px solid #363636",
                          borderRadius: "6px",
                          padding: "10px",
                        }}
                      >
                        <option value="Free">Free</option>
                        <option value="Fixed Price">Fixed Price</option>
                      </select>
                      <input
                        type="number"
                        placeholder="Price"
                        step={0.1}
                        value={mediaPrice ?? ""}
                        onChange={e => {
                          const value = e.target.value;
                          if (value === "") {
                            setMediaPrice(undefined);
                          } else {
                            const price = Number(value);
                            if (!isNaN(price) && price >= 0) {
                              setMediaPrice(price);
                            }
                          }
                        }}
                        disabled={!isPaidMedia}
                        style={{
                          width: "48%",
                          backgroundColor: "#1e1e1e",
                          color: "#fff",
                          border: `1px solid ${isPaidMedia ? "#363636" : "#555"}`,
                          borderRadius: "6px",
                          padding: "10px",
                          opacity: isPaidMedia ? 1 : 0.5,
                        }}
                      />
                    </Box>
                  </DialogContent>
                  <DialogActions>
                    <Button
                      className={classes.selectButton}
                      onClick={() => {
                        setMediaDialogVisible(false);
                        setSelectedFiles([]);
                        setMediaPreviewUrl(null);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      className={classes.selectButton}
                      onClick={async () => {
                        await handleSendMessage();
                        setMediaDialogVisible(false);
                        setSelectedFiles([]);
                        setMediaPreviewUrl(null);
                      }}
                      disabled={
                        isUploadingMedia ||
                        (isPaidMedia &&
                          (mediaPrice === undefined || mediaPrice <= 0))
                      }
                      style={{
                        opacity: isUploadingMedia ? 0.5 : 1,
                      }}
                    >
                      {isUploadingMedia ? (
                        <Box
                          display="flex"
                          alignItems="center"
                          style={{ gap: "8px" }}
                        >
                          <CircularProgress
                            size={16}
                            style={{ color: "#fff" }}
                          />
                          Sending...
                        </Box>
                      ) : (
                        "Send"
                      )}
                    </Button>
                  </DialogActions>
                </Box>
              </Modal>
              <UploadMediaPostModal
                open={uploadMediaModalOpen}
                onClose={() => setUploadMediaModalOpen(false)}
                onConfirm={(files: File[]) => {
                  if (files.length > 0) {
                    setUploadMediaModalOpen(false);
                    setSelectedFiles(files);
                    setMediaPreviewUrl(URL.createObjectURL(files[0]));
                    setMediaType(
                      files[0].type.startsWith("video/") ? "video" : "image"
                    );
                    setIsPaidMedia(false);
                    setMediaPrice(undefined);
                  }
                }}
                showPaidContent={false}
                showAgreement={false}
                maxFiles={10}
              />
              {/* <Modal
                open={blockModalOpen}
                onClose={() => setBlockModalOpen(false)}
                aria-labelledby="block-user-modal-title"
                aria-describedby="block-user-modal-description"
              >
                <Box className={classes.modalTipWrapper}>
                  <DialogTitle
                    id="block-user-modal-title"
                    style={{ color: "#FE5A97" }}
                  >
                    Block User
                  </DialogTitle>
                  <DialogContent>
                    <DialogContentText
                      id="block-user-modal-description"
                      style={{ color: "#fff" }}
                    >
                      Are you sure you want to block this user? They will no
                      longer be able to send you messages.
                    </DialogContentText>
                  </DialogContent>
                  <DialogActions>
                    <Button
                      onClick={() => setBlockModalOpen(false)}
                      className={classes.cancelButton}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={async () => {
                        if (blockUserId) {
                          try {
                            await blockUserAPI(blockUserId);
                            showToast({
                              message: "User blocked successfully",
                              success: true,
                            });
                            // Optionally, update UI (e.g., remove conversation or disable input)
                            setConversations(prev =>
                              prev.filter(
                                conv => conv.participant?._id !== blockUserId
                              )
                            );
                            if (
                              selectedConversationId === currentConversation?._id
                            ) {
                              setSelectedConversationId(null);
                              history.push("/messages");
                            }
                          } catch (error) {
                            showToast({
                              message: "Failed to block user",
                              err: true,
                            });
                          }
                        }
                        setBlockModalOpen(false);
                        setBlockUserId(null);
                      }}
                      className={classes.sendButtonHelp}
                    >
                      Block
                    </Button>
                  </DialogActions>
                </Box>
              </Modal> */}
              <Modal
                open={deleteConversationModalOpen}
                onClose={() => setDeleteConversationModalOpen(false)}
                aria-labelledby="delete-conversation-modal-title"
                aria-describedby="delete-conversation-modal-description"
              >
                <Box className={classes.modalTipWrapper}>
                  <DialogTitle
                    id="delete-conversation-modal-title"
                    style={{ color: "#f44336" }}
                  >
                    Delete Conversation
                  </DialogTitle>
                  <DialogContent>
                    <DialogContentText
                      id="delete-conversation-modal-description"
                      style={{ color: "#fff" }}
                    >
                      Are you sure you want to delete this conversation? This
                      action cannot be undone and all messages will be
                      permanently removed.
                    </DialogContentText>
                  </DialogContent>
                  <DialogActions>
                    <Button
                      onClick={() => setDeleteConversationModalOpen(false)}
                      className={classes.cancelButton}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={() => handleDeleteConversation()}
                      className={classes.sendButtonHelp}
                      style={{ backgroundColor: "#f44336", color: "#fff" }}
                    >
                      Delete
                    </Button>
                  </DialogActions>
                </Box>
              </Modal>
              <Modal
                open={true}
                disablePortal
                hideBackdrop
                disableEnforceFocus
                disableScrollLock
                className={classes.modalSubscriptionWrapper}
                style={{
                  pointerEvents: purchaseMediaDialogVisible ? "auto" : "none",
                }}
              >
                <Box
                  className={classes.modalTipWrapper}
                  style={{
                    display: purchaseMediaDialogVisible ? "block" : "none",
                  }}
                >
                  <DialogTitle className={classes.modalTitle}>
                    Purchase{" "}
                    {selectedMessageForPurchase?.type === "video"
                      ? "Video"
                      : "Image"}
                  </DialogTitle>
                  <DialogContent className={classes.modalContent}>
                    <Typography className={classes.modalSubtitle}>
                      Unlock this{" "}
                      {selectedMessageForPurchase?.type === "video"
                        ? "video"
                        : "image"}{" "}
                      for ${selectedMessageForPurchase?.price}.
                    </Typography>
                    {selectedMessageForPurchase && (
                      <CCBillPayment
                        price={selectedMessageForPurchase.price}
                        context="message-purchase"
                        title={`Unlock ${selectedMessageForPurchase.type === "video"
                          ? "Video"
                          : "Image"
                          }`}
                        buttonText={`Pay $${selectedMessageForPurchase.price} to Unlock`}
                        description={`Unlock this ${selectedMessageForPurchase.type === "video"
                          ? "video"
                          : "image"
                          } for $${selectedMessageForPurchase.price}`}
                        onProceed={handleCompletePurchase}
                        userId={userId}
                        email={(user as any)?.email}
                        messageId={selectedMessageForPurchase._id}
                        tipForUserId={recipientProfile?._id}
                      />
                    )}
                  </DialogContent>
                  <DialogActions className={classes.modalActions}>
                    <Button
                      onClick={() => {
                        setPurchaseMediaDialogVisible(false);
                        setSelectedMessageForPurchase(null);
                      }}
                      className={classes.cancelButton}
                    >
                      Cancel
                    </Button>
                  </DialogActions>
                </Box>
              </Modal>
              <TipDialog
                open={tipDialogVisible && !!recipientProfile}
                onClose={() => setTipDialogVisible(false)}
                suggestedTips={recipientProfile?.suggestedTips}
                minimumTipAmount={recipientProfile?.minimumTipAmount}
                username={recipientProfile?.firstName}
                creatorName={
                  recipientProfile
                    ? `${recipientProfile.firstName} ${recipientProfile.lastName}`
                    : undefined
                }
                tipForUserId={recipientProfile?._id}
                userId={userId}
                email={(user as any)?.email}
                conversationId={selectedConversationId || undefined}
                onPaymentSuccess={handleTipSuccess}
                validateBeforeStep1={amount => {
                  if (!recipientProfile?._id || !userId || amount <= 0) {
                    showToast("Please enter a valid tip amount.", "error");
                    return false;
                  }
                  return true;
                }}
              />
              <Modal
                open={true}
                disablePortal
                hideBackdrop
                disableEnforceFocus
                disableScrollLock
                className={classes.modalSubscriptionWrapper}
                style={{ pointerEvents: requestVideoDialog ? "auto" : "none" }}
              >
                <Box
                  className={classes.modalTipWrapper}
                  style={{ display: requestVideoDialog ? "block" : "none" }}
                >
                  <CustomVideoRequestDialog
                    onClose={() => setRequestVideoDialog(false)}
                    onSubmit={async (data: any) => {
                      try {
                        // Send video request to backend
                        const response = await sendVideoRequestAPI({
                          userId: (user as any)?.id,
                          creatorId: recipientProfile?._id, // Changed from modelId to creatorId
                          conversationId: selectedConversationId,
                          description: data.description,
                          price: data.price,
                          estimatedDuration: data.estimatedDuration || 5, // Default 5 minutes
                          specialInstructions: data.specialInstructions || "",
                          tags: data.tags || [],
                          transactionId: `vr_${Date.now()}_${Math.random()
                            .toString(36)
                            .substr(2, 9)}`, // Generate unique transaction ID
                        });

                        // The backend will create the message automatically
                        // We just need to refresh the conversation to show the new message
                        if (selectedConversationId) {
                          // Update conversation list to move to top
                          setConversations(prev => {
                            const updated = [...prev];
                            const index = updated.findIndex(
                              c => c._id === selectedConversationId
                            );
                            if (index !== -1) {
                              const currentConv = updated[index];
                              updated[index] = {
                                ...currentConv,
                                lastMessage: {
                                  content: "Custom video request",
                                  senderId: userId || "",
                                  type: "video_request",
                                  createdAt: new Date().toISOString(),
                                },
                              };
                              const movedConv = updated.splice(index, 1)[0];
                              updated.unshift(movedConv);
                              return updated.sort((a, b) => {
                                const timeA = new Date(
                                  a.lastMessage.createdAt
                                ).getTime();
                                const timeB = new Date(
                                  b.lastMessage.createdAt
                                ).getTime();
                                const validTimeA = isNaN(timeA) ? 0 : timeA;
                                const validTimeB = isNaN(timeB) ? 0 : timeB;
                                return validTimeB - validTimeA;
                              });
                            }
                            return updated;
                          });
                        }

                        showToast("Video request sent successfully!");
                        setRequestVideoDialog(false);
                      } catch (e: any) {
                        const errorMessage =
                          e?.response?.data?.message ||
                          e?.message ||
                          "Error sending video request!";
                        showToast(errorMessage, "error");
                      }
                    }}
                    price={recipientProfile?.customContentPrice || 100}
                    modelId={recipientProfile?._id}
                    conversationId={selectedConversationId || undefined}
                    recipientId={recipientProfile?._id}
                  />
                </Box>
              </Modal>
            </Grid>
          </Grid>
          <Modal
            open={openImage}
            onClose={handleCloseImage}
            aria-labelledby="modal-title"
            aria-describedby="modal-description"
          >
            <Box className={classes.modalImage}>
              <img
                src={imageUrl}
                alt="Message content"
                style={{ width: "100%", height: "auto", objectFit: "contain" }}
              />
            </Box>
          </Modal>
          <UpgradeModal
            isOpen={showUpgradeModal}
            onClose={() => setShowUpgradeModal(false)}
            creditsRemaining={creditsRemaining}
            freeMessagesRemaining={freeMessagesRemaining}
            freeMessagesLimit={freeMessagesLimit}
          />
        </Box>
      </Box >
    </>
  );
};

export default MessagesPage;
